import { useCallback, useEffect, useRef } from 'react';
import type { CardDefId, CellId, GameEvent, GameState, InstanceId, PlayerId, Side, Zone } from '@cards/engine';
import { HIDDEN_CARD_DEF_ID, MELTDOWN_HEAT_THRESHOLD, applyEvent, effectiveStats, opposing } from '@cards/engine';
import { getCardUi } from '@cards/cards';
import type { LogEntry } from '../types.js';
import {
  type CombatCue,
  type CastCue,
  type CellCue,
  type EffectCue,
  type EffectPathCue,
  type HackCue,
  type MoveCue,
  type PassivePulseCue,
  type PlaybackNotice,
  type RandomPickCue,
  type HostAbsorbCue,
  type SpawnCue,
  type AssemblyCue,
  type TurnBanner,
  type TurnPlaybackStore,
  type TurnPlaybackVisuals,
  type SkipChanges,
} from '../ui/TurnPlayback.js';
import { MOTION_DURATIONS } from '../ui/kit/tokens.js';
import { deckPassiveIdForPlayer } from '../ui/deckPassiveView.js';
import { currentPlaybackTiming as timing } from '../ui/playbackSpeed.js';
import { m14PlaybackNotice } from '../ui/m14View.js';
import { playerDisplayName, type PlayerNames } from '../ui/locale.js';
import { formatPublicPlaybackEvent } from '../ui/publicPlayback.js';

interface PlaybackBatch {
  state: GameState;
  entries: LogEntry[];
}

type UnitDiedEvent = Extract<GameEvent, { type: 'UnitDied' }>;

/**
 * Maturation spends a public counter, not health.  Spending it used to reuse
 * the generic negative-counter cue, which puts a red `-N` over the unit and
 * makes maturation look like a health loss. The status badge already reflects
 * the new counter value; keep the journal notice but do not play a
 * damage-shaped card cue for the spend.
 */
function isMaturationCounterSpend(event: Extract<GameEvent, { type: 'EffectCounterChanged' }>): boolean {
  return event.reason === 'maturation' && event.delta < 0;
}

interface PendingPlaybackDeath {
  event: UnitDiedEvent;
  followers: GameEvent[];
}

/** Events whose reducer needs a destination cell to be physically empty. */
function claimedCell(event: GameEvent): CellId | undefined {
  switch (event.type) {
    case 'TokenCreated': return event.cellId;
    // batch-first FIELD (D53+): планируемая карта занимает ячейку через UnitPlaced;
    // парный UnitEnteredPlay — уже идемпотентное Появление.
    case 'UnitPlaced': return event.cellId;
    case 'UnitEnteredPlay': return event.cellId;
    case 'UnitMoved': return event.to;
    case 'CaptureApplied': return event.toCellId;
    case 'CaptureReturned': return event.toCellId;
    default: return undefined;
  }
}

/** Lifecycle events are observable boundaries after which a death chain is over. */
function endsDeferredDeathChain(event: GameEvent): boolean {
  switch (event.type) {
    case 'UnitDied':
    case 'CombatEnded':
    case 'RoundEnded':
    case 'PhaseChanged':
    case 'CardResolved':
    case 'CardFizzled':
      return true;
    default:
      return false;
  }
}

/**
 * The mutable half of TurnPlaybackStore: playback steps patch it imperatively
 * and subscribers are notified once per actual change. Keeping this outside
 * React state means a replay step no longer re-renders the whole game tree —
 * consumers pick their slice via useTurnPlaybackSelector.
 */
interface InternalPlaybackStore extends TurnPlaybackStore {
  patch(partial: Partial<TurnPlaybackVisuals>): void;
  setState(next: GameState): void;
  updateVisibleLog(updater: (current: readonly LogEntry[]) => readonly LogEntry[]): void;
}

/**
 * The board cards and seats a batch changed, for the ring a skipped batch
 * leaves behind: a card that arrived, moved, or whose numbers, heat, freeze,
 * hack, vars or effects differ; a seat whose health differs.
 */
export function skippedChanges(before: GameState, after: GameState): SkipChanges {
  const signature = (state: GameState, id: InstanceId): string => {
    const instance = state.instances[id]!;
    const stats = effectiveStats(state, id);
    const effects = state.effects.filter((effect) => effect.scope.kind === 'unit' && effect.scope.instanceId === id);
    return JSON.stringify([instance.cellId, stats.attack, stats.health, stats.maxHealth,
      instance.heat ?? 0, instance.freeze ?? 0, instance.hack ?? null, instance.vars, effects]);
  };
  const instanceIds = Object.values(after.instances)
    .filter((instance) => instance.zone === 'board' && instance.cellId !== undefined)
    .filter((instance) => before.instances[instance.id]?.zone !== 'board'
      || signature(before, instance.id) !== signature(after, instance.id))
    .map((instance) => instance.id);
  const playerIds = Object.values(after.players)
    .filter((player) => before.players[player.id]?.health !== player.health)
    .map((player) => player.id);
  return { instanceIds, playerIds };
}

/**
 * Перегрев and Заморозка play as their own families (03 §4.6–4.7), not as a
 * generic good/bad effect: a rise is `negative`, a fall `positive`, a frozen
 * card's spent attack `skip` — each carrying the ±N it moved.
 */
function thermalCue(event: Extract<GameEvent, { type: 'HeatChanged' | 'FreezeChanged' }>): EffectCue {
  return {
    kind: event.type === 'HeatChanged' ? 'heat' : 'freeze',
    phase: event.type === 'FreezeChanged' && event.reason === 'combat_skip'
      ? 'skip'
      : event.current < event.previous ? 'positive' : 'negative',
    targetInstanceId: event.instanceId,
    delta: event.current - event.previous,
  };
}

function createPlaybackStore(
  initialState: GameState,
  initialLog: readonly LogEntry[],
  skip: () => void,
): InternalPlaybackStore {
  const listeners = new Set<() => void>();
  let visuals: TurnPlaybackVisuals = {
    isPlaying: false,
    banner: undefined,
    enteringId: undefined,
    faceDownIds: [],
    oneShotLineup: [],
    drawCue: undefined,
    faceDownCells: [],
    withheldFieldEntryIds: [],
    lineupHandoffIds: [],
    fieldFizzleCue: undefined,
    fracturedIds: [],
    departedIds: [],
    dyingId: undefined,
    consumingIds: [],
    transformingId: undefined,
    timedDestroyingId: undefined,
    notice: undefined,
    combatCue: undefined,
    effectCue: undefined,
    effectPathCue: undefined,
    castCue: undefined,
    cellCue: undefined,
    moveCue: undefined,
    spawnCue: undefined,
    hostAbsorbCue: undefined,
    assemblyCue: undefined,
    hackCue: undefined,
    randomPickCue: undefined,
    passivePulse: undefined,
    skipChanges: undefined,
    meltingId: undefined,
    skip,
  };
  let state = initialState;
  let visibleLog: readonly LogEntry[] = [...initialLog];
  const notify = (): void => {
    for (const listener of [...listeners]) listener();
  };
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getVisuals: () => visuals,
    getState: () => state,
    getVisibleLog: () => visibleLog,
    patch(partial) {
      let changed = false;
      for (const key of Object.keys(partial) as (keyof TurnPlaybackVisuals)[]) {
        if (!Object.is(visuals[key], partial[key])) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
      visuals = { ...visuals, ...partial };
      notify();
    },
    setState(next) {
      state = next;
      notify();
    },
    updateVisibleLog(updater) {
      const next = updater(visibleLog);
      if (next === visibleLog) return;
      visibleLog = next;
      notify();
    },
  };
}

export interface TurnPlaybackOptions {
  /** Viewer-relative public names used by notices; transport ids are never rendered. */
  viewerId?: PlayerId;
  playerNames?: PlayerNames;
}

function isAnimatedBatch(events: readonly GameEvent[]): boolean {
  return events.some((event) =>
    m14PlaybackNotice(event) !== undefined ||
    event.type === 'CardRevealed' ||
    event.type === 'CardResolved' ||
    event.type === 'CardFizzled' ||
    event.type === 'CardDrawn' ||
    event.type === 'UnitEnteredPlay' ||
    event.type === 'RoundEnded' ||
    event.type === 'CombatStarted' ||
    event.type === 'HeatChanged' ||
    event.type === 'FreezeChanged' ||
    event.type === 'ShieldAbsorbed' ||
    event.type === 'ShieldDepleted' ||
    event.type === 'AttackAvoidanceChecked' ||
    event.type === 'AttackResolved' ||
    event.type === 'DamageDealt' ||
    event.type === 'PlayerHealthRestored' ||
    event.type === 'UnitHealed' ||
    event.type === 'ResourcesChanged' ||
    event.type === 'EffectDurationChanged' ||
    event.type === 'RoundStarted' ||
    event.type === 'RuleChanged' ||
    event.type === 'VarChanged' ||
    event.type === 'MatchEnded' ||
    event.type === 'UnitDied' ||
    event.type === 'TokenCreated' ||
    event.type === 'CardCreated' ||
    event.type === 'TokenCreationSkipped' ||
    event.type === 'CardConsumed' ||
    event.type === 'CardTransformed' ||
    event.type === 'AssemblyCompleted' ||
    event.type === 'TemporaryEnergyGranted' ||
    event.type === 'TemporaryEnergyExpired' ||
    event.type === 'TimedDestroyScheduled' ||
    event.type === 'TimedDestroyResolved' ||
    event.type === 'HackApplied' ||
    event.type === 'HackTriggered' ||
    event.type === 'HackRemoved' ||
    event.type === 'UnitMoved' ||
    event.type === 'StatChanged' ||
    event.type === 'EffectAdded' ||
    event.type === 'EffectCounterChanged' ||
    event.type === 'EffectRemoved' ||
    event.type === 'CardsAbsorbed' ||
    event.type === 'RandomDamageReplacementResolved' ||
    event.type === 'RandomUnitSelectionResolved'
  );
}

/**
 * Keep only self-contained packages.  Mirror retaliation may move beside its
 * strike only when the engine supplied an explicit causal edge; generic damage
 * is left in reducer order and uses visual-only causal previews below.
 */
export function presentationEvents(events: readonly GameEvent[]): GameEvent[] {
  const shieldOrdered: GameEvent[] = [];
  let shieldPackage: GameEvent[] | undefined;
  for (const event of events) {
    if (shieldPackage === undefined && event.type === 'ShieldAbsorbed') {
      shieldPackage = [event];
      continue;
    }
    if (shieldPackage !== undefined) {
      if (event.type === 'DamageDealt') {
        shieldOrdered.push(event, ...shieldPackage);
        shieldPackage = undefined;
      } else {
        shieldPackage.push(event);
      }
      continue;
    }
    shieldOrdered.push(event);
  }
  if (shieldPackage !== undefined) shieldOrdered.push(...shieldPackage);

  // A source may give Growth to several plants synchronously. The engine must
  // retain that FIFO order for rules, so each threshold reaction queues behind
  // the grants. For presentation, however, show the transformation immediately
  // after the counter that unlocked it. A maturation package is self-identifying
  // without a card-id branch: CardTransformed is followed by its `maturation`
  // counter spend, and that spend points back to the positive counter event by
  // effect id and unit scope.
  const movedMaturations = new Set<GameEvent>();
  const maturationsByGrowth = new Map<GameEvent, GameEvent[]>();
  for (let index = 0; index < shieldOrdered.length; index += 1) {
    const transform = shieldOrdered[index];
    const spend = shieldOrdered[index + 1];
    if (transform?.type !== 'CardTransformed'
      || spend?.type !== 'EffectCounterChanged'
      || spend.scope.kind !== 'unit'
      || spend.scope.instanceId !== transform.instanceId
      || !isMaturationCounterSpend(spend)) continue;
    const growth = shieldOrdered.slice(0, index).reverse().find((candidate) =>
      candidate.type === 'EffectCounterChanged'
      && candidate.effectId === spend.effectId
      && candidate.scope.kind === 'unit'
      && candidate.scope.instanceId === transform.instanceId
      && candidate.delta > 0,
    );
    if (growth === undefined) continue;
    movedMaturations.add(transform);
    movedMaturations.add(spend);
    const pending = maturationsByGrowth.get(growth) ?? [];
    pending.push(transform, spend);
    maturationsByGrowth.set(growth, pending);
  }

  const maturationOrdered: GameEvent[] = [];
  for (const event of shieldOrdered) {
    if (movedMaturations.has(event)) continue;
    maturationOrdered.push(event, ...(maturationsByGrowth.get(event) ?? []));
  }

  const movedRetaliations = new Set<GameEvent>();
  const retaliationsByCause = new Map<GameEvent, GameEvent[]>();
  for (const retaliation of maturationOrdered) {
    if (retaliation.type !== 'DamageDealt'
      || retaliation.origin !== 'effect'
      || retaliation.combat === true
      || retaliation.causedByEventIndex === undefined) continue;
    // causedByEventIndex addresses the authoritative input batch, not this
    // small Shield-only presentation permutation.
    const cause = events[retaliation.causedByEventIndex];
    if (cause?.type !== 'DamageDealt'
      || cause.attackGroupId === undefined
      || cause.target.kind !== 'unit'
      || retaliation.sourceId !== cause.target.instanceId
      || retaliation.target.kind !== 'unit'
      || retaliation.target.instanceId !== cause.sourceId) continue;
    movedRetaliations.add(retaliation);
    const pending = retaliationsByCause.get(cause) ?? [];
    pending.push(retaliation);
    retaliationsByCause.set(cause, pending);
  }
  const ordered: GameEvent[] = [];
  for (const event of maturationOrdered) {
    if (movedRetaliations.has(event)) continue;
    ordered.push(event, ...(retaliationsByCause.get(event) ?? []));
  }
  return ordered;
}

interface ReactivePreview {
  readonly events: readonly GameEvent[];
  /** A reducer-safe pulse; its owning event still replays at stream position. */
  readonly cue?: EffectCue;
  /** Cell effects and token creation may have no live card to pulse. */
  readonly cellCue?: CellCue;
  /** A draw can safely materialize its hand card before its paired CardMoved. */
  readonly draw?: Extract<GameEvent, { type: 'CardDrawn' }>;
  /** Suppress the later duplicate visual beat, never the reducer application. */
  readonly suppressAtStream?: boolean;
  /** Show the public event name beside the causal parent. */
  readonly announce?: boolean;
}

/**
 * Build visual reactions from public causal edges. StatChanged remains at its
 * literal reducer point: combat now drains each exchange before the next one,
 * so the true state mutation itself is already timely for both gameplay and
 * playback.
 */
export function causalReactionPreviews(events: readonly GameEvent[]): ReadonlyMap<GameEvent, readonly ReactivePreview[]> {
  type PendingPreview = {
    parent: GameEvent;
    targetInstanceId: InstanceId;
    type: 'stat' | 'var';
    phase: 'positive' | 'negative';
    events: GameEvent[];
  };
  const pending: PendingPreview[] = [];
  const latestByTarget = new Map<string, PendingPreview>();

  for (let index = 0; index < events.length; index += 1) {
    const event = events[index]!;
    const parentIndex = event.causedByEventIndex;
    if (parentIndex === undefined || !Number.isInteger(parentIndex) || parentIndex < 0 || parentIndex >= index) continue;
    const parent = events[parentIndex];
    if (parent === undefined) continue;

    let type: PendingPreview['type'];
    let targetInstanceId: InstanceId;
    if (event.type === 'StatChanged') continue;
    if (event.type === 'VarChanged' && event.public === true && event.target.kind === 'unit') {
      type = 'var';
      targetInstanceId = event.target.instanceId;
    } else {
      continue;
    }
    const phase: PendingPreview['phase'] = 'positive';
    const key = `${parentIndex}\u0000${type}\u0000${targetInstanceId}`;
    const previous = latestByTarget.get(key);
    const current: PendingPreview = previous !== undefined && previous.phase === phase
      ? previous
      : { parent, targetInstanceId, type, phase, events: [] };
    if (current !== previous) {
      pending.push(current);
      latestByTarget.set(key, current);
    }
    current.events.push(event);
  }

  const result = new Map<GameEvent, ReactivePreview[]>();
  for (const preview of pending) {
    let cue: EffectCue;
    if (preview.type === 'var') {
      cue = { kind: 'effect', phase: 'positive', targetInstanceId: preview.targetInstanceId };
    } else {
      const changes = preview.events as Extract<GameEvent, { type: 'StatChanged' }>[];
      const sum = (stat: 'attack' | 'health' | 'maxHealth'): number => changes.reduce(
        (total, event) => total + (event.stat === stat ? event.delta : 0), 0);
      const attack = sum('attack');
      const health = sum('health');
      const shownHealth = health !== 0 ? health : sum('maxHealth');
      cue = {
        kind: attack === 0 && shownHealth > 0 ? 'heal' : 'stat',
        phase: preview.phase,
        targetInstanceId: preview.targetInstanceId,
        statDeltas: {
          ...(attack === 0 ? {} : { attack }),
          ...(shownHealth === 0 ? {} : { health: shownHealth }),
        },
      };
    }
    const previews = result.get(preview.parent) ?? [];
    previews.push({ events: preview.events, cue });
    result.set(preview.parent, previews);
  }

  const append = (parent: GameEvent, preview: ReactivePreview): void => {
    const previews = result.get(parent) ?? [];
    previews.push(preview);
    result.set(parent, previews);
  };
  for (let index = 0; index < events.length; index += 1) {
    const event = events[index]!;
    const parentIndex = event.causedByEventIndex;
    if (parentIndex === undefined || !Number.isInteger(parentIndex) || parentIndex < 0 || parentIndex >= index) continue;
    const parent = events[parentIndex];
    if (parent === undefined) continue;
    switch (event.type) {
      case 'CardDrawn':
        append(parent, { events: [event], draw: event, suppressAtStream: true, announce: true });
        break;
      case 'UnitHealed':
        append(parent, {
          events: [event],
          cue: { kind: 'heal', phase: 'positive', targetInstanceId: event.instanceId, delta: event.amount },
          suppressAtStream: true,
          announce: true,
        });
        break;
      case 'HeatChanged':
      case 'FreezeChanged':
        append(parent, {
          events: [event],
          cue: thermalCue(event),
          suppressAtStream: true,
          announce: true,
        });
        break;
      case 'EffectAdded': {
        const negative = event.effect.statMods?.some((mod) =>
          (mod.op === 'add' && mod.value < 0) || (mod.op === 'mul' && mod.value < 1),
        ) === true;
        if (event.effect.scope.kind === 'unit') {
          append(parent, {
            events: [event],
            cue: {
              kind: event.effect.shield === undefined ? 'effect' : 'shield',
              phase: event.effect.shield === undefined ? (negative ? 'negative' : 'positive') : 'impact',
              targetInstanceId: event.effect.scope.instanceId,
            },
            suppressAtStream: true,
            announce: true,
          });
        } else if (event.effect.scope.kind === 'player') {
          append(parent, {
            events: [event],
            cue: { kind: 'effect', phase: 'positive', targetPlayerId: event.effect.scope.playerId },
            suppressAtStream: true,
            announce: true,
          });
        } else if (event.effect.scope.kind === 'cell') {
          append(parent, {
            events: [event],
            cellCue: { cellId: event.effect.scope.cellId, kind: 'effect', phase: 'positive' },
            suppressAtStream: true,
            announce: true,
          });
        }
        break;
      }
      case 'EffectCounterChanged':
        if (event.scope.kind === 'unit') {
          if (isMaturationCounterSpend(event)) break;
          append(parent, {
            events: [event],
            cue: {
              kind: event.reason === 'photosynthesis' ? 'sun' : 'counter',
              phase: event.delta >= 0 ? 'positive' : 'negative',
              targetInstanceId: event.scope.instanceId,
              delta: event.delta,
            },
            suppressAtStream: true,
            announce: true,
          });
        } else if (event.scope.kind === 'cell') {
          append(parent, {
            events: [event],
            cellCue: {
              cellId: event.scope.cellId,
              kind: 'effect',
              phase: event.delta >= 0 ? 'positive' : 'negative',
              delta: event.delta,
            },
            suppressAtStream: true,
            announce: true,
          });
        }
        break;
      case 'TokenCreated':
        if (event.cellId !== undefined) {
          // The token's reducer remains late because it may occupy a cell used
          // by another frozen attack.  This early cell pulse names the cause;
          // the actual card still materializes at its authoritative position.
          append(parent, {
            events: [event],
            cellCue: { cellId: event.cellId, kind: 'creation', phase: 'positive' },
            announce: true,
          });
        }
        break;
      case 'DamageDealt':
        // Crooked Mirror is a self-contained package: presentationEvents moves
        // this explicitly linked reflection to its attack and it owns a full
        // death-fall path there. Do not add a second generic pulse first.
        if (parent.type === 'DamageDealt'
          && parent.attackGroupId !== undefined
          && parent.target.kind === 'unit'
          && event.origin === 'effect'
          && event.combat !== true
          && event.sourceId === parent.target.instanceId
          && event.target.kind === 'unit'
          && event.target.instanceId === parent.sourceId) break;
        append(parent, {
          events: [event],
          cue: {
            kind: 'damage', phase: 'impact',
            ...(event.target.kind === 'unit'
              ? { targetInstanceId: event.target.instanceId }
              : { targetPlayerId: event.target.playerId }),
          },
          announce: true,
        });
        break;
      case 'CardsAbsorbed':
        append(parent, {
          events: [event],
          cue: { kind: 'effect', phase: 'positive', targetInstanceId: event.receiver.instanceId },
          announce: true,
        });
        break;
      default:
        break;
    }
  }
  return result;
}

function noticeFor(
  event: GameEvent,
  labelPlayer: (playerId: PlayerId) => string,
  viewerSide?: Side,
): PlaybackNotice {
  const label = formatPublicPlaybackEvent(event, { labelPlayer, ...(viewerSide !== undefined ? { viewerSide } : {}) });
  return { kind: label.kind, text: label.text };
}

interface ActiveOneShot {
  instanceId: InstanceId;
}

function changedPublicVarCues(
  before: GameState,
  after: GameState,
): Array<{ instanceId: InstanceId; phase: 'positive' | 'negative' }> {
  const cues: Array<{ instanceId: InstanceId; phase: 'positive' | 'negative' }> = [];
  for (const next of Object.values(after.instances)) {
    if (next.zone !== 'board' || next.defId === HIDDEN_CARD_DEF_ID) continue;
    const previous = before.instances[next.id];
    const publicVars = getCardUi(next.defId)?.publicVars;
    if (previous === undefined || publicVars === undefined) continue;
    for (const projection of Object.values(publicVars)) {
      const oldValue = previous.vars[projection.key];
      const newValue = next.vars[projection.key];
      if (Object.is(oldValue, newValue)) continue;
      cues.push({
        instanceId: next.id,
        phase: typeof oldValue === 'number' && typeof newValue === 'number' && newValue < oldValue
          ? 'negative'
          : 'positive',
      });
      break;
    }
  }
  return cues;
}

function hydrateRevealedInstance(draft: GameState, finalState: GameState, instanceId: InstanceId): boolean {
  const current = draft.instances[instanceId];
  const revealed = finalState.instances[instanceId];
  if (current === undefined || revealed === undefined || current.defId !== HIDDEN_CARD_DEF_ID) return false;
  current.defId = revealed.defId;
  current.stats = structuredClone(revealed.stats);
  current.vars = structuredClone(revealed.vars);
  return true;
}

function zoneIds(state: GameState, playerId: PlayerId, zone: 'hand' | 'armed'): InstanceId[] | undefined {
  const player = state.players[playerId];
  if (player === undefined) return undefined;
  return zone === 'hand' ? player.hand : player.armedReactions;
}

/**
 * Privacy snapshots use zone-scoped aliases for hidden opponent cards and omit
 * the authoritative id entirely. At the first public lifecycle event we can
 * safely replace one indistinguishable back in that zone with the now-public
 * instance. This restores the canonical id before reducer playback sees the
 * following CardMoved event.
 */
function materializePublicInstance(
  draft: GameState,
  finalState: GameState,
  playerId: PlayerId,
  instanceId: InstanceId,
  from: 'hand' | 'armed',
): boolean {
  if (draft.instances[instanceId] !== undefined) {
    return hydrateRevealedInstance(draft, finalState, instanceId);
  }
  const revealed = finalState.instances[instanceId];
  const ids = zoneIds(draft, playerId, from);
  if (revealed === undefined || ids === undefined) return false;
  const aliasIndex = ids.findIndex((id) => draft.instances[id]?.defId === HIDDEN_CARD_DEF_ID);
  if (aliasIndex < 0) return false;
  const aliasId = ids[aliasIndex]!;
  const materialized = structuredClone(revealed);
  materialized.zone = from as Zone;
  materialized.controllerId = materialized.ownerId;
  delete materialized.cellId;
  delete materialized.enteredRound;
  delete materialized.hack;
  delete draft.instances[aliasId];
  draft.instances[instanceId] = materialized;
  ids[aliasIndex] = instanceId;
  return true;
}

/**
 * Puts a drawn card into the draft hand at the moment of `CardDrawn`.
 *
 * `CardDrawn` is a reducer no-op: the zone change rides on the paired deck→hand
 * `CardMoved`, which is emitted AFTER it and which redaction drops outright (it
 * would tie a deck alias to an authoritative id). So at cue time the card is
 * not in the displayed hand — unredacted it arrives one event late, redacted it
 * arrives only with the authoritative snapshot at the end of the batch, long
 * after the cue window closed. Either way the hand has nothing to animate and
 * the card simply pops in. Materializing here is what gives the draw-in an
 * element to start from.
 */
function materializeDrawnInstance(
  draft: GameState,
  finalState: GameState,
  playerId: PlayerId,
  instanceId: InstanceId,
): boolean {
  const player = draft.players[playerId];
  if (player === undefined || player.hand.includes(instanceId)) return false;
  const deckIndex = player.deck.indexOf(instanceId);
  if (deckIndex >= 0 && draft.instances[instanceId] !== undefined) {
    // Unredacted feed: the authoritative instance is already the deck entry, so
    // this is exactly the move the paired CardMoved would have performed.
    player.deck.splice(deckIndex, 1);
    player.hand.push(instanceId);
    draft.instances[instanceId]!.zone = 'hand';
    return true;
  }
  const drawn = finalState.instances[instanceId];
  if (drawn === undefined || player.deck.length === 0) return false;
  // Redacted feed: one of this player's indistinguishable deck aliases IS this
  // card. Retire the alias that carries the same definition — the viewer's own
  // deck has its definitions revealed, so any other choice would blink an
  // unrelated card out of the remaining-deck browser for the draw's duration.
  const sameDefIndex = player.deck.findIndex((id) => draft.instances[id]?.defId === drawn.defId);
  const aliasIndex = sameDefIndex < 0 ? 0 : sameDefIndex;
  const aliasId = player.deck[aliasIndex]!;
  const materialized = structuredClone(drawn);
  materialized.zone = 'hand';
  materialized.controllerId = materialized.ownerId;
  delete materialized.cellId;
  delete materialized.enteredRound;
  delete materialized.hack;
  delete draft.instances[aliasId];
  player.deck.splice(aliasIndex, 1);
  draft.instances[instanceId] = materialized;
  player.hand.push(instanceId);
  return true;
}

function rewindRevealedStats(
  draft: GameState,
  instanceId: InstanceId,
  events: readonly GameEvent[],
): void {
  const stats = draft.instances[instanceId]?.stats;
  if (stats === undefined) return;
  // The final snapshot contains post-combat health. Rewind public stat deltas
  // so replaying the event stream does not apply damage twice to a card that
  // was hidden before reveal.
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index]!;
    if (event.type === 'DamageDealt' && event.target.kind === 'unit' && event.target.instanceId === instanceId) {
      stats.health += event.amount;
    } else if (event.type === 'StatChanged' && event.instanceId === instanceId) {
      if (event.stat === 'attack' || event.stat === 'health' || event.stat === 'maxHealth') {
        stats[event.stat] -= event.delta;
      }
    }
  }
}

function hydrateRevealCandidates(
  draft: GameState,
  finalState: GameState,
  events: readonly GameEvent[],
): void {
  const revealedIds = new Set(
    events.flatMap((event) => event.type === 'UnitEnteredPlay' ? [event.instanceId] : []),
  );
  for (const instanceId of revealedIds) {
    if (!hydrateRevealedInstance(draft, finalState, instanceId)) continue;
    rewindRevealedStats(draft, instanceId, events);
  }
}

function sourceCellFor(
  draft: GameState,
  knownCells: ReadonlyMap<InstanceId, CellId>,
  event: Extract<GameEvent, { type: 'DamageDealt' }>,
): CellId | undefined {
  return event.sourceId === undefined
    ? undefined
    : draft.instances[event.sourceId]?.cellId ?? knownCells.get(event.sourceId);
}

function areReciprocalOrdinaryCombatParts(
  first: Extract<GameEvent, { type: 'DamageDealt' }>,
  second: Extract<GameEvent, { type: 'DamageDealt' }>,
): boolean {
  return first.origin === 'ordinary_attack'
    && second.origin === 'ordinary_attack'
    && first.combat === true
    && second.combat === true
    && first.sourceId !== undefined
    && second.sourceId !== undefined
    && first.target.kind === 'unit'
    && second.target.kind === 'unit'
    && first.target.instanceId === second.sourceId
    && second.target.instanceId === first.sourceId
    && (first.partIndex ?? 0) === (second.partIndex ?? 0);
}

/**
 * Turns authoritative event batches into a deliberately slower presentation state.
 * The engine remains authoritative: after every batch the exact server snapshot
 * replaces the replay draft, including state changes not represented visually.
 */
export function useTurnPlayback(
  authoritativeState: GameState,
  seq: number,
  log: readonly LogEntry[],
  options?: TurnPlaybackOptions,
): TurnPlaybackStore {
  const hackCuesByAttackerRef = useRef(new Map<InstanceId, HackCue>());
  const viewerIdRef = useRef(options?.viewerId);
  viewerIdRef.current = options?.viewerId;
  const playerNamesRef = useRef(options?.playerNames);
  playerNamesRef.current = options?.playerNames;

  const displayedStateRef = useRef(authoritativeState);
  const lastQueuedSeqRef = useRef(seq);
  const queueRef = useRef<PlaybackBatch[]>([]);
  const visibleLogIndicesRef = useRef(new Set(log.map((entry) => entry.index)));
  const pendingLogIndicesRef = useRef(new Set<number>());
  const drainingRef = useRef(false);
  const mountedRef = useRef(false);
  const skipRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const delayResolverRef = useRef<(() => void) | undefined>(undefined);
  const skipChangesTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const skip = useCallback((): void => {
    skipRef.current = true;
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    timerRef.current = undefined;
    const resolve = delayResolverRef.current;
    delayResolverRef.current = undefined;
    resolve?.();
  }, []);

  const storeRef = useRef<InternalPlaybackStore | undefined>(undefined);
  storeRef.current ??= createPlaybackStore(authoritativeState, log, skip);
  const store = storeRef.current;

  // Shims keeping the playback script below verbatim: every cue assignment is
  // a store patch instead of a React state update.
  const setIsPlaying = (value: boolean): void => store.patch({ isPlaying: value });
  const setBanner = (value: TurnBanner | undefined): void => store.patch({ banner: value });
  const setEnteringId = (value: InstanceId | undefined): void => store.patch({ enteringId: value });
  const setFaceDownIds = (value: readonly InstanceId[]): void => store.patch({ faceDownIds: value });
  const setOneShotLineup = (
    value: TurnPlaybackVisuals['oneShotLineup']
      | ((current: TurnPlaybackVisuals['oneShotLineup']) => TurnPlaybackVisuals['oneShotLineup']),
  ): void => store.patch({
    oneShotLineup: typeof value === 'function' ? value(store.getVisuals().oneShotLineup) : value,
  });
  const setDrawCue = (value: TurnPlaybackVisuals['drawCue']): void => store.patch({ drawCue: value });
  const setFaceDownCells = (
    value: readonly CellId[] | ((current: readonly CellId[]) => readonly CellId[]),
  ): void => store.patch({
    faceDownCells: typeof value === 'function' ? value(store.getVisuals().faceDownCells) : value,
  });
  const setWithheldFieldEntryIds = (
    value: readonly InstanceId[] | ((current: readonly InstanceId[]) => readonly InstanceId[]),
  ): void => store.patch({
    withheldFieldEntryIds: typeof value === 'function'
      ? value(store.getVisuals().withheldFieldEntryIds)
      : value,
  });
  const setLineupHandoffIds = (value: readonly InstanceId[]): void =>
    store.patch({ lineupHandoffIds: value });
  const setFieldFizzleCue = (value: TurnPlaybackVisuals['fieldFizzleCue']): void =>
    store.patch({ fieldFizzleCue: value });
  const removeOneFaceDownCell = (cells: readonly CellId[], cellId: CellId): readonly CellId[] => {
    const index = cells.indexOf(cellId);
    return index < 0 ? cells : [...cells.slice(0, index), ...cells.slice(index + 1)];
  };
  const setFracturedIds = (value: readonly InstanceId[]): void => store.patch({ fracturedIds: value });
  const setDepartedIds = (value: readonly InstanceId[]): void => store.patch({ departedIds: value });
  const setDyingId = (value: InstanceId | undefined): void => store.patch({ dyingId: value });
  const setConsumingIds = (
    value: InstanceId[] | ((current: readonly InstanceId[]) => InstanceId[]),
  ): void => store.patch({
    consumingIds: typeof value === 'function' ? value(store.getVisuals().consumingIds) : value,
  });
  const setTransformingId = (value: InstanceId | undefined): void => store.patch({ transformingId: value });
  const setTimedDestroyingId = (value: InstanceId | undefined): void => store.patch({ timedDestroyingId: value });
  const setNotice = (value: PlaybackNotice | undefined): void => store.patch({ notice: value });
  const setCombatCue = (value: CombatCue | undefined): void => store.patch({ combatCue: value });
  const setEffectCue = (value: EffectCue | undefined): void => store.patch({ effectCue: value });
  const setEffectPathCue = (value: EffectPathCue | undefined): void => store.patch({ effectPathCue: value });
  const setCastCue = (value: CastCue | undefined): void => store.patch({ castCue: value });
  const setCellCue = (value: CellCue | undefined): void => store.patch({ cellCue: value });
  const setMoveCue = (value: MoveCue | undefined): void => store.patch({ moveCue: value });
  const setSpawnCue = (value: SpawnCue | undefined): void => store.patch({ spawnCue: value });
  const setHostAbsorbCue = (value: HostAbsorbCue | undefined): void => store.patch({ hostAbsorbCue: value });
  const setAssemblyCue = (value: AssemblyCue | undefined): void => store.patch({ assemblyCue: value });
  const setHackCue = (value: HackCue | undefined): void => store.patch({ hackCue: value });
  const setRandomPickCue = (value: RandomPickCue | undefined): void => store.patch({ randomPickCue: value });
  const setPassivePulse = (value: PassivePulseCue | undefined): void => store.patch({ passivePulse: value });
  const drawCueSequenceRef = useRef(0);

  const showState = useCallback((next: GameState): void => {
    displayedStateRef.current = next;
    if (mountedRef.current) store.setState(next);
  }, [store]);

  const delay = useCallback((ms: number): Promise<void> => {
    if (skipRef.current) return Promise.resolve();
    return new Promise((resolve) => {
      delayResolverRef.current = resolve;
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        delayResolverRef.current = undefined;
        resolve();
      }, ms);
    });
  }, []);

  const revealLogEntry = useCallback((entry: LogEntry): void => {
    pendingLogIndicesRef.current.delete(entry.index);
    if (visibleLogIndicesRef.current.has(entry.index)) return;
    visibleLogIndicesRef.current.add(entry.index);
    if (mountedRef.current) store.updateVisibleLog((current) => [...current, entry]);
  }, [store]);

  const drain = useCallback(async (): Promise<void> => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    while (queueRef.current.length > 0 && mountedRef.current) {
      const batch = queueRef.current.shift();
      if (batch === undefined) break;
      skipRef.current = false;
      const events = batch.entries.map((entry) => entry.event);

      if (!isAnimatedBatch(events)) {
        for (const entry of batch.entries) revealLogEntry(entry);
        showState(batch.state);
        continue;
      }

      setIsPlaying(true);
      if (skipChangesTimerRef.current !== undefined) clearTimeout(skipChangesTimerRef.current);
      skipChangesTimerRef.current = undefined;
      store.patch({ skipChanges: undefined });
      const batchStart = displayedStateRef.current;
      const draft = structuredClone(displayedStateRef.current);
      hydrateRevealCandidates(draft, batch.state, events);
      const animatedTokenIds = new Set<InstanceId>();
      const knownCells = new Map<InstanceId, CellId>();
      for (const instance of Object.values(draft.instances)) {
        if (instance.cellId !== undefined) knownCells.set(instance.id, instance.cellId);
      }
      const absorptionReceiverByDonor = new Map<InstanceId, InstanceId>();
      const consumptionReceiverByDonor = new Map<InstanceId, InstanceId>();
      // A devourer destroys its prey outright instead of consuming it, so the
      // animation needs an explicit source → prey link. UnitDied.sourceId is
      // the honest one: it survives a devour with no attack behind it
      // (summoning delay, freeze, an attack redirected elsewhere). The
      // attack relationship stays as a fallback for destroys whose executor
      // did not name itself.
      const devourerByPrey = new Map<InstanceId, InstanceId>();
      const noteDevourer = (preyId: InstanceId, devourerId: InstanceId): void => {
        const devourer = draft.instances[devourerId] ?? batch.state.instances[devourerId];
        if (devourer === undefined
          || getCardUi(devourer.defId)?.mechanics?.includes('devour') !== true) return;
        devourerByPrey.set(preyId, devourerId);
      };
      const revealedSourceDefs = new Map<InstanceId, CardDefId>();
      for (const event of events) {
        if (event.type === 'AttackPerformed' && event.target.kind === 'unit') {
          noteDevourer(event.target.instanceId, event.attackerId);
        }
        // Emitted after the attack, so an attributed death wins over the guess
        // drawn from whoever the devourer happened to swing at.
        if (event.type === 'UnitDied' && event.sourceId !== undefined) {
          noteDevourer(event.instanceId, event.sourceId);
        }
        if (event.type !== 'CardsAbsorbed') continue;
        for (const donor of event.donors) {
          absorptionReceiverByDonor.set(donor.instanceId, event.receiver.instanceId);
          consumptionReceiverByDonor.set(donor.instanceId, event.receiver.instanceId);
          knownCells.set(donor.instanceId, donor.cellId);
        }
      }
      // Some generic consume flows predate CardsAbsorbed. Infer their receiver
      // only when the FIELD card whose appearance is currently resolving
      // subsequently gains stats in the same resolution, rather than treating
      // every consumed card as absorbed. CardRevealed is intentionally not the
      // boundary here: all FIELD cards reveal before any of their appearances,
      // so it would incorrectly associate an onAppear consume with the last
      // card in the reveal lineup.
      let inferredFieldSource: InstanceId | undefined;
      let pendingConsumedIds: InstanceId[] = [];
      for (const event of events) {
        if (event.type === 'UnitEnteredPlay') {
          inferredFieldSource = event.instanceId;
          pendingConsumedIds = [];
        } else if (event.type === 'CardConsumed'
          && !absorptionReceiverByDonor.has(event.instanceId)
          && inferredFieldSource !== undefined
          && event.instanceId !== inferredFieldSource) {
          pendingConsumedIds.push(event.instanceId);
        } else if (event.type === 'StatChanged'
          && event.instanceId === inferredFieldSource
          && pendingConsumedIds.length > 0) {
          for (const donorId of pendingConsumedIds) {
            consumptionReceiverByDonor.set(donorId, inferredFieldSource);
          }
        // CardResolved is enqueued immediately after UnitEnteredPlay, before
        // that event's onAppear hooks drain. UnitAppeared, on the other hand,
        // is emitted only after the entire appearance cascade (including its
        // queued consumes) has completed.
        } else if ((event.type === 'UnitAppeared' || event.type === 'CardFizzled')
          && event.instanceId === inferredFieldSource) {
          inferredFieldSource = undefined;
          pendingConsumedIds = [];
        }
      }
      const explainedLethalIds = new Set<InstanceId>();
      const deathIdsInBatch = new Set(events.flatMap((event) => event.type === 'UnitDied' ? [event.instanceId] : []));
      let currentStage: TurnBanner | undefined;
      let activeOneShot: ActiveOneShot | undefined;
      let activeResolutionSource: { instanceId: InstanceId; defId: CardDefId; kind: 'FIELD' | 'SPELL' | 'REACTION' | 'VIRUS' } | undefined;
      // CardResolved is emitted before a FIELD card's onAppear cascade. Keep
      // this narrower window until UnitAppeared closes that cascade, so an
      // attributed on-appear effect still knows which physical card acted.
      let activeAppearanceSource: InstanceId | undefined;
      // The overlay host ghost currently mounted (see the CardConsumed overlay
      // branch). Held here so its CardsAbsorbed can attach the stats the
      // swallow moved, and so the flip that eats it knows whether those numbers
      // are on screen and need a beat to be read.
      let overlayHostGhost: HostAbsorbCue | undefined;
      const pendingDeaths: PendingPlaybackDeath[] = [];
      const assemblyByFragmentId = new Map<InstanceId, Extract<GameEvent, { type: 'AssemblyCompleted' }>>();
      const playedAssemblyFragments = new Set<InstanceId>();
      for (const candidate of events) {
        if (candidate.type !== 'AssemblyCompleted') continue;
        for (const fragmentId of candidate.fragmentIds) assemblyByFragmentId.set(fragmentId, candidate);
      }
      const fracturedIds = new Set<InstanceId>();
      const departedIds = new Set<InstanceId>();
      // Prey pulled into a devourer has already played its entire
      // disappearance inside the devour cue, so it must skip the generic
      // dying beat in dissolvePendingDeaths — a second fade would read as
      // the card dying twice.
      const devouredIds = new Set<InstanceId>();
      const deathAnimationsStartedWithSpawn = new Set<InstanceId>();
      // Fragments already applied and animated as part of a decay burst; their
      // own TokenCreated events are skipped when the loop reaches them.
      const burstConsumedTokenIds = new Set<InstanceId>();
      const syncFracturedIds = (): void => setFracturedIds([...fracturedIds]);
      const syncDepartedIds = (): void => setDepartedIds([...departedIds]);
      const fracture = (instanceId: InstanceId): void => {
        if (fracturedIds.has(instanceId)) return;
        fracturedIds.add(instanceId);
        syncFracturedIds();
      };
      const depart = (instanceId: InstanceId): void => {
        if (departedIds.has(instanceId)) return;
        departedIds.add(instanceId);
        syncDepartedIds();
      };
      const dissolvePendingDeaths = async (cellId?: CellId): Promise<void> => {
        const selected = pendingDeaths.filter((pending) => cellId === undefined || pending.event.cellId === cellId);
        for (const pending of selected) {
          const index = pendingDeaths.indexOf(pending);
          if (index >= 0) pendingDeaths.splice(index, 1);
          // A lethal DamageDealt has already spent the full attack-impact beat
          // in the fractured state. Do not add a second 650 ms death hold: at
          // the next lifecycle boundary the card should leave the cell. Deaths
          // without a preceding visible lethal hit keep their own animation.
          const deathWasShownAtImpact = explainedLethalIds.has(pending.event.instanceId);
          if (!deathWasShownAtImpact
            && !deathAnimationsStartedWithSpawn.has(pending.event.instanceId)
            && !devouredIds.has(pending.event.instanceId)) {
            if (pending.event.cause === 'timed') {
              setTimedDestroyingId(pending.event.instanceId);
              await delay(timing().timedDestroy);
            } else {
              // Перегрев 4 destroys the card where it stands: it melts instead.
              const melting = (draft.instances[pending.event.instanceId]?.heat ?? 0) >= MELTDOWN_HEAT_THRESHOLD;
              if (melting) store.patch({ meltingId: pending.event.instanceId });
              setDyingId(pending.event.instanceId);
              await delay(timing().death);
            }
          }
          applyEvent(draft, pending.event);
          for (const follower of pending.followers) applyEvent(draft, follower);
          fracturedIds.delete(pending.event.instanceId);
          departedIds.delete(pending.event.instanceId);
          devouredIds.delete(pending.event.instanceId);
          syncFracturedIds();
          syncDepartedIds();
          showState(structuredClone(draft));
          setDyingId(undefined);
          store.patch({ meltingId: undefined });
          setTimedDestroyingId(undefined);
        }
      };
      const labelPlayer = (playerId: PlayerId): string =>
        playerDisplayName(playerId, viewerIdRef.current, playerNamesRef.current);
      const viewerSide: Side | undefined = viewerIdRef.current === undefined
        ? undefined
        : displayedStateRef.current.players[viewerIdRef.current]?.side;
      try {
        const entryByEvent = new Map(batch.entries.map((entry) => [entry.event, entry]));
        const orderedEvents = presentationEvents(events);
        const pendingFieldBackCells = new Map<InstanceId, CellId>(
          orderedEvents.flatMap((candidate) =>
            candidate.type === 'CardRevealed' && candidate.kind === 'FIELD'
              ? [[candidate.instanceId, candidate.cellId] as const]
              : []),
        );
        const releaseFieldBack = (instanceId: InstanceId): boolean => {
          const cellId = pendingFieldBackCells.get(instanceId);
          if (cellId === undefined) return false;
          pendingFieldBackCells.delete(instanceId);
          setFaceDownCells((cells) => removeOneFaceDownCell(cells, cellId));
          setWithheldFieldEntryIds((ids) => ids.filter((id) => id !== instanceId));
          return true;
        };
        const pairedDamageEvents = new Set<GameEvent>();
        const pairedAvoidedAttackResolvedEvents = new Set<GameEvent>();
        const successfulAvoidancesBySourceId = new Map<InstanceId, Extract<GameEvent, { type: 'AttackAvoidanceChecked' }>>();
        const mergedStatEvents = new Set<GameEvent>();
        // hand→discard moves already spent inside a FIELD fizzle beat, so the
        // generic CardMoved case does not replay them a beat later (see the
        // CardFizzled case).
        const spentFizzleDiscards = new Set<GameEvent>();
        const reactivePreviewsByParent = causalReactionPreviews(events);
        const previewedReactionEvents = new Set<GameEvent>(
          [...reactivePreviewsByParent.values()].flatMap((previews) =>
            previews.flatMap((preview) => preview.suppressAtStream === true
              || preview.events.every((event) => event.type === 'StatChanged'
                || (event.type === 'VarChanged' && event.public === true))
              ? preview.events
              : [])),
        );
        // Ongoing auras reconcile immediately after UnitPlaced, while a FIELD
        // card is deliberately still withheld behind its face-down lineup card.
        // Applying one here would grow its allies before the source is visible;
        // worse, the attack-lunge has no mounted source card to animate. Hold
        // source-bound unit effects until that source has completed its own
        // UnitEnteredPlay flip. This changes presentation order only: the
        // engine's event order and final authoritative snapshot remain intact.
        const deferredFieldEntryEffects = new Map<InstanceId, Extract<GameEvent, { type: 'EffectAdded' }>[]>()
        const shouldDeferFieldEntryEffect = (candidate: Extract<GameEvent, { type: 'EffectAdded' }>): boolean =>
          candidate.effect.sourceId !== undefined
          && candidate.effect.scope.kind === 'unit'
          && pendingFieldBackCells.has(candidate.effect.sourceId);
        const playEffectAdded = async (effectEvent: Extract<GameEvent, { type: 'EffectAdded' }>): Promise<void> => {
          // Deferred entries did not reveal their log row at their literal
          // stream position, so reveal it together with the visual beat.
          const effectEntry = entryByEvent.get(effectEvent);
          if (effectEntry !== undefined) revealLogEntry(effectEntry);
          const source = effectEvent.effect.sourceId === undefined
            ? undefined
            : draft.instances[effectEvent.effect.sourceId];
          const target = effectEvent.effect.scope.kind === 'unit'
            ? draft.instances[effectEvent.effect.scope.instanceId]
            : undefined;
          // A source-bound positive aura has an actor and a receiver. Play
          // that relationship as the familiar attack lunge, while the impact
          // communicates its actual result: stats increase rather than damage.
          // This is intentionally data-driven so authored ongoing buffs
          // (including Clockwork Ottoman) need no defId UI branch.
          const positiveStatMods = effectEvent.effect.statMods?.filter((mod) =>
            mod.op === 'add' && mod.value > 0,
          ) ?? [];
          const buffStrike = !previewedReactionEvents.has(effectEvent)
            && source !== undefined
            && target !== undefined
            && source.id !== target.id
            && source.zone === 'board'
            && target.zone === 'board'
            && source.cellId !== undefined
            && target.cellId !== undefined
            && positiveStatMods.length > 0
            ? {
                sourceInstanceId: source.id,
                sourceCellId: source.cellId,
                targetCellId: target.cellId,
                targetInstanceId: target.id,
                amount: 0,
              }
            : undefined;
          if (buffStrike !== undefined) {
            setCombatCue({ ...buffStrike, phase: 'travel' });
            await delay(timing().attackTravel);
          }
          applyEvent(draft, effectEvent);
          showState(structuredClone(draft));
          if (previewedReactionEvents.has(effectEvent)) return;
          if (effectEvent.effect.scope.kind === 'unit') {
            const negative = effectEvent.effect.statMods?.some((mod) =>
              (mod.op === 'add' && mod.value < 0) || (mod.op === 'mul' && mod.value < 1),
            ) === true;
            if (buffStrike !== undefined) {
              const attack = positiveStatMods.reduce((sum, mod) =>
                sum + (mod.stat === 'attack' ? mod.value : 0), 0);
              const health = positiveStatMods.reduce((sum, mod) =>
                sum + (mod.stat === 'maxHealth' ? mod.value : 0), 0);
              setCombatCue({ ...buffStrike, phase: 'impact' });
              setEffectCue({
                kind: 'stat', phase: 'positive', targetInstanceId: buffStrike.targetInstanceId,
                statDeltas: {
                  ...(attack === 0 ? {} : { attack }),
                  ...(health === 0 ? {} : { health }),
                },
              });
              await delay(timing().attackImpact);
              setCombatCue(undefined);
              setEffectCue(undefined);
              return;
            }
            setEffectCue({
              kind: effectEvent.effect.shield === undefined ? 'effect' : 'shield',
              phase: effectEvent.effect.shield === undefined ? (negative ? 'negative' : 'positive') : 'impact',
              targetInstanceId: effectEvent.effect.scope.instanceId,
            });
            // Armed Structures buffs a freshly-eligible STRUCTURE: pulse the
            // owner's badge alongside the card's own +ATK cue.
            const buffOwnerId = draft.instances[effectEvent.effect.scope.instanceId]?.controllerId;
            if (buffOwnerId !== undefined
              && deckPassiveIdForPlayer(draft, buffOwnerId) === effectEvent.effect.defId) {
              setPassivePulse({ playerId: buffOwnerId, variant: 'buff' });
            }
          } else if (effectEvent.effect.scope.kind === 'player') {
            setEffectCue({
              kind: 'effect',
              phase: 'positive',
              targetPlayerId: effectEvent.effect.scope.playerId,
            });
          } else if (effectEvent.effect.scope.kind === 'cell') {
            setCellCue({
              cellId: effectEvent.effect.scope.cellId,
              kind: 'effect',
              phase: 'positive',
            });
          }
          if (activeOneShot !== undefined) {
            setNotice(noticeFor(effectEvent, labelPlayer, viewerSide));
          }
          if (effectEvent.effect.scope.kind === 'unit' || effectEvent.effect.scope.kind === 'player'
            || effectEvent.effect.scope.kind === 'cell' || activeOneShot !== undefined) {
            await delay(timing().statusChange);
          }
          setNotice(undefined);
          setEffectCue(undefined);
          setCellCue(undefined);
          setPassivePulse(undefined);
        };
        const playReactivePreviews = async (parent: GameEvent): Promise<void> => {
          for (const preview of reactivePreviewsByParent.get(parent) ?? []) {
            const source = preview.events[0];
            if (preview.announce === true && source !== undefined) setNotice(noticeFor(source, labelPlayer, viewerSide));
            if (preview.draw !== undefined) {
              materializeDrawnInstance(draft, batch.state, preview.draw.playerId, preview.draw.instanceId);
              setDrawCue({
                playerId: preview.draw.playerId,
                instanceId: preview.draw.instanceId,
                sequence: ++drawCueSequenceRef.current,
              });
              showState(structuredClone(draft));
              const drawnForViewer = viewerIdRef.current === undefined
                || preview.draw.playerId === viewerIdRef.current;
              await delay(drawnForViewer
                ? timing().cardDraw
                : timing().hiddenHudStep);
              setDrawCue(undefined);
            } else if (preview.cue !== undefined) {
              setEffectCue(preview.cue);
              await delay(preview.cue.kind === 'stat' ? timing().statBuff : timing().statusChange);
              setEffectCue(undefined);
            } else if (preview.cellCue !== undefined) {
              setCellCue(preview.cellCue);
              await delay(timing().cellPulse);
              setCellCue(undefined);
            }
            if (preview.announce === true) setNotice(undefined);
          }
        };
        /**
         * Decay bursts should read as a consequence of the killing blow, but
         * the engine's simultaneous death wave emits UnitDied and the fragment
         * TokenCreated events only after every attack of the phase resolved.
         * When the dying unit is the batch's only decay death and nothing that
         * still plays before the burst's own stream position claims a fragment
         * cell, the burst is pulled forward to the lethal impact; the deferred
         * TokenCreated events later replay as consumed no-ops. Any ambiguity
         * falls back to the in-place burst at the death wave.
         */
        const playEarlyDecayBurst = async (deadId: InstanceId, impactIndex: number): Promise<void> => {
          const defId = draft.instances[deadId]?.defId ?? batch.state.instances[deadId]?.defId;
          if (defId === undefined || getCardUi(defId)?.mechanics?.includes('decay') !== true) return;
          // Fragment TokenCreated events carry no source id, so attribution is
          // only unambiguous while the batch holds a single decay death.
          const decayDeaths = orderedEvents.filter(
            (candidate): candidate is Extract<GameEvent, { type: 'UnitDied' }> =>
              candidate.type === 'UnitDied'
              && getCardUi(candidate.defId)?.mechanics?.includes('decay') === true);
          if (decayDeaths.length !== 1) return;
          const death = decayDeaths[0]!;
          if (death.instanceId !== deadId || death.cellId === undefined) return;
          const deathIndex = orderedEvents.indexOf(death);
          if (deathIndex <= impactIndex) return;
          let burstStart = -1;
          for (let index = deathIndex + 1; index < orderedEvents.length; index += 1) {
            const candidate = orderedEvents[index]!;
            if (candidate.type === 'TokenCreated' && candidate.cellId !== undefined
              && getCardUi(candidate.defId)?.mechanics?.includes('fragment') === true) {
              burstStart = index;
              break;
            }
            if (candidate.type === 'PhaseChanged' || candidate.type === 'CombatEnded'
              || candidate.type === 'RoundEnded') break;
          }
          if (burstStart === -1) return;
          const burst: Extract<GameEvent, { type: 'TokenCreated' }>[] = [];
          const burstIds = new Set<InstanceId>();
          for (let index = burstStart; index < orderedEvents.length; index += 1) {
            const next = orderedEvents[index]!;
            if (next.type === 'TokenCreated' && next.cellId !== undefined
              && getCardUi(next.defId)?.mechanics?.includes('fragment') === true) {
              burst.push(next);
              burstIds.add(next.instanceId);
              continue;
            }
            if ((next.type === 'UnitEnteredPlay' || next.type === 'UnitAppeared')
              && burstIds.has(next.instanceId)) continue;
            break;
          }
          // The engine assigned these cells after its death sweep freed the
          // board, so playing early is only safe while they are already empty
          // and stay unclaimed by everything up to the burst's own position.
          if (burst.some((created) => draft.board.cells[created.cellId!]?.occupantId != null)) return;
          for (let index = impactIndex + 1; index < burstStart; index += 1) {
            const cell = claimedCell(orderedEvents[index]!);
            if (cell !== undefined && burst.some((created) => created.cellId === cell)) return;
          }
          for (const created of burst) {
            applyEvent(draft, created);
            animatedTokenIds.add(created.instanceId);
            knownCells.set(created.instanceId, created.cellId!);
            burstConsumedTokenIds.add(created.instanceId);
          }
          // No setDyingId here: a combat-lethal source has already fractured
          // and departed at its impact, so only the burst itself plays.
          setSpawnCue({
            sourceCellId: death.cellId,
            targetInstanceIds: burst.map((created) => created.instanceId),
            decayFragment: true,
          });
          setNotice(noticeFor(burst[0]!, labelPlayer, viewerSide));
          setEnteringId(burst[0]!.instanceId);
          showState(structuredClone(draft));
          await delay(timing().decayFragment);
          setEnteringId(undefined);
          setSpawnCue(undefined);
          setNotice(undefined);
        };
        for (let eventIndex = 0; eventIndex < orderedEvents.length; eventIndex += 1) {
          const event = orderedEvents[eventIndex]!;
          if (pairedDamageEvents.has(event) || pairedAvoidedAttackResolvedEvents.has(event) || mergedStatEvents.has(event)
            || spentFizzleDiscards.has(event)) continue;
          if (!mountedRef.current) break;
          if (endsDeferredDeathChain(event) && pendingDeaths.length > 0) {
            await dissolvePendingDeaths();
          } else {
            const cellId = claimedCell(event);
            if (cellId !== undefined && pendingDeaths.some((pending) => pending.event.cellId === cellId)) {
              await dissolvePendingDeaths(cellId);
            }
          }
          const deferFieldEntryEffect = event.type === 'EffectAdded'
            && shouldDeferFieldEntryEffect(event);
          const entry = entryByEvent.get(event);
          if (!deferFieldEntryEffect && entry !== undefined) revealLogEntry(entry);
          switch (event.type) {
            case 'CardRevealed': {
              const materialized = materializePublicInstance(
                draft,
                batch.state,
                event.playerId,
                event.instanceId,
                event.kind === 'REACTION' ? 'armed' : 'hand',
              );
              if (materialized && event.kind === 'FIELD') {
                rewindRevealedStats(draft, event.instanceId, events);
                knownCells.set(event.instanceId, event.cellId);
              }
              applyEvent(draft, event);
              const revealedDefId = draft.instances[event.instanceId]?.defId
                ?? batch.state.instances[event.instanceId]?.defId;
              if (revealedDefId !== undefined && revealedDefId !== HIDDEN_CARD_DEF_ID) {
                revealedSourceDefs.set(event.instanceId, revealedDefId);
                activeResolutionSource = {
                  instanceId: event.instanceId,
                  defId: revealedDefId,
                  kind: event.kind,
                };
              }
              if (event.kind === 'SPELL' || event.kind === 'VIRUS' || event.kind === 'REACTION') {
                const oneShotKind = event.kind;
                setOneShotLineup((cards) => {
                  const revealed = {
                    instanceId: event.instanceId,
                    playerId: event.playerId,
                    ...(revealedDefId === undefined ? {} : { defId: revealedDefId }),
                    kind: oneShotKind,
                    faceDown: false,
                    revealing: true,
                  } as const;
                  return cards.some((card) => card.instanceId === event.instanceId)
                    ? cards.map((card) => card.instanceId === event.instanceId ? { ...card, ...revealed } : card)
                    : [...cards, revealed];
                });
              }
              showState(structuredClone(draft));
              if (event.kind === 'SPELL' || event.kind === 'VIRUS' || event.kind === 'REACTION') {
                const reactionDuringCombat = event.kind === 'REACTION' && currentStage === 'combat';
                if (!reactionDuringCombat && currentStage !== 'effects') {
                  currentStage = 'effects';
                  setBanner('effects');
                }
                activeOneShot = { instanceId: event.instanceId };
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hiddenHudStep);
              setNotice(undefined);
              break;
            }
            case 'PhaseChanged':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (event.phase === 'resolution') {
                currentStage = 'reveal';
                setBanner('reveal');
                // Every committed FIELD intent — successful or fizzled — shows
                // one public face-down back on its revealed destination. This
                // keeps both viewers symmetric and prevents the lineup itself
                // from leaking a future fizzle. Each terminal outcome releases
                // only its own duplicate cell entry.
                const lineupCells: CellId[] = [];
                const withheldFieldEntryIds: InstanceId[] = [];
                const oneShotLineup: Array<TurnPlaybackVisuals['oneShotLineup'][number]> = [];
                for (let i = eventIndex + 1; i < orderedEvents.length; i += 1) {
                  const upcoming = orderedEvents[i]!;
                  if (upcoming.type === 'CombatStarted') break;
                  if (upcoming.type === 'CardRevealed'
                    && upcoming.kind === 'FIELD') {
                    lineupCells.push(upcoming.cellId);
                    withheldFieldEntryIds.push(upcoming.instanceId);
                  } else if (upcoming.type === 'CardRevealed') {
                    if (upcoming.kind === 'SPELL'
                      || upcoming.kind === 'VIRUS'
                      || upcoming.kind === 'REACTION') {
                      oneShotLineup.push({
                        instanceId: upcoming.instanceId,
                        playerId: upcoming.playerId,
                        kind: upcoming.kind,
                        faceDown: true,
                        revealing: false,
                      });
                    }
                  }
                }
                setOneShotLineup(oneShotLineup);
                setWithheldFieldEntryIds(withheldFieldEntryIds);
                if (lineupCells.length > 0 || oneShotLineup.length > 0) {
                  setFaceDownCells(lineupCells);
                  showState(structuredClone(draft));
                  await delay(timing().revealLineup);
                } else {
                  await delay(timing().hiddenHudStep);
                }
              }
              break;
            case 'ResourcesChanged':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hiddenHudStep);
              setNotice(undefined);
              break;
            case 'EffectDurationChanged': {
              const effect = draft.effects.find((candidate) => candidate.id === event.effectId);
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (effect?.scope.kind === 'unit') {
                setEffectCue({ kind: 'effect', phase: 'trigger', targetInstanceId: effect.scope.instanceId });
              } else if (effect?.scope.kind === 'player') {
                setEffectCue({ kind: 'effect', phase: 'trigger', targetPlayerId: effect.scope.playerId });
              } else if (effect?.scope.kind === 'cell') {
                setCellCue({ cellId: effect.scope.cellId, kind: 'effect', phase: 'positive' });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              setCellCue(undefined);
              break;
            }
            case 'RoundStarted':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              break;
            case 'RuleChanged':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hiddenHudStep);
              setNotice(undefined);
              break;
            case 'VarChanged': {
              applyEvent(draft, event);
              showState(structuredClone(draft));
              const instanceId = event.target.kind === 'unit' ? event.target.instanceId : undefined;
              const instance = instanceId === undefined ? undefined : draft.instances[instanceId];
              const isDeclaredPublic = instance !== undefined
                && Object.values(getCardUi(instance.defId)?.publicVars ?? {}).some((projection) => projection.key === event.key);
              if (isDeclaredPublic && instanceId !== undefined && !previewedReactionEvents.has(event)) {
                setEffectCue({ kind: 'effect', phase: 'positive', targetInstanceId: instanceId });
                await delay(timing().statusChange);
                setEffectCue(undefined);
              }
              break;
            }
            case 'MatchEnded':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              break;
            case 'UnitEnteredPlay': {
              hydrateRevealedInstance(draft, batch.state, event.instanceId);
              applyEvent(draft, event);
              knownCells.set(event.instanceId, event.cellId);
              if (event.cause === 'played') activeAppearanceSource = event.instanceId;
              if (animatedTokenIds.has(event.instanceId)) {
                // Tokens have no CardRevealed FIELD intent and therefore never
                // own a lineup back, even when they enter a queued destination.
                showState(structuredClone(draft));
                break;
              }
              // Marvel Snap-style reveal: the card holds face-down for a beat,
              // the flip narrows it edge-on, then the face swaps in and pops with
              // an accent. enteringId drives the flip keyframe (which bakes in the
              // hold); faceDownIds picks which side (back vs face) is drawn and is
              // cleared at the edge-on point (~60% of the flip).
              // A receiver landing on the host it just ate runs the longer
              // cards-reveal-card-devouring: the SAME flip, played where the card
              // has been lying all along — on the host, shifted down — and only
              // then the gulp that eats the host and climbs the card into the
              // slot. Two beats, one CSS clock, so the flip is over and read
              // before anything is eaten (the ghost above waits for it via
              // animation-delay). The face still swaps at the flip's edge-on
              // frame, which is the same ~370ms in either case.
              const swallowing = overlayHostGhost?.cellId === event.cellId;
              const revealMs = swallowing
                ? timing().overlayReveal
                : timing().cardReveal;
              const flipSwap = Math.round(timing().cardReveal * 0.6);
              setEnteringId(event.instanceId);
              setFaceDownIds([event.instanceId]);
              // Hand off this cell from the lineup placeholder to the real card
              // (both drawn face-down, so the swap is seamless) and flip it up.
              if (releaseFieldBack(event.instanceId)) {
                setLineupHandoffIds([event.instanceId]);
              }
              showState(structuredClone(draft));
              await delay(flipSwap);
              setFaceDownIds([]);
              await delay(revealMs - flipSwap);
              setEnteringId(undefined);
              setLineupHandoffIds([]);
              const deferredEffects = deferredFieldEntryEffects.get(event.instanceId) ?? [];
              deferredFieldEntryEffects.delete(event.instanceId);
              for (const effectEvent of deferredEffects) await playEffectAdded(effectEvent);
              // An overlay host's ghost (set at its CardConsumed) was swallowed
              // into this flip — release it together with its consume notice.
              // When the swallow actually moved stats, the +N pips rose out of
              // the gulp near the end of the flip and get the same beat any
              // other stat change gets before the cue that owns them clears.
              if (overlayHostGhost?.cellId === event.cellId
                && overlayHostGhost.gained !== undefined) {
                await delay(timing().statusChange);
              }
              overlayHostGhost = undefined;
              setHostAbsorbCue(undefined);
              setNotice(undefined);
              break;
            }
            case 'CombatStarted':
              applyEvent(draft, event);
              currentStage = 'combat';
              setBanner('combat');
              // The reveal stage is over — clear any lineup placeholder that a
              // non-flipping entry (e.g. a token) might have left face-down.
              setFaceDownCells([]);
              setOneShotLineup([]);
              showState(structuredClone(draft));
              await delay(timing().hiddenHudStep);
              break;
            case 'HeatChanged':
            case 'FreezeChanged':
            case 'ShieldAbsorbed':
            case 'ShieldDepleted':
              if ((event.type === 'HeatChanged' || event.type === 'FreezeChanged') && previewedReactionEvents.has(event)) {
                applyEvent(draft, event);
                showState(structuredClone(draft));
                break;
              }
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (event.type === 'HeatChanged' || event.type === 'FreezeChanged') {
                setEffectCue(thermalCue(event));
              } else {
                setEffectCue({
                  kind: 'shield',
                  phase: event.type === 'ShieldDepleted' ? 'deplete' : 'impact',
                  targetInstanceId: event.targetInstanceId,
                });
              }
              if (event.type === 'HeatChanged' || event.type === 'FreezeChanged' || activeOneShot !== undefined) {
                setNotice(noticeFor(event, labelPlayer, viewerSide));
              }
              await delay(timing().statusChange);
              if (event.type === 'HeatChanged' || event.type === 'FreezeChanged' || activeOneShot !== undefined) setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'AttackAvoidanceChecked':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (event.succeeded && event.sourceId !== undefined) {
                successfulAvoidancesBySourceId.set(event.sourceId, event);
              }
              if (event.target.kind === 'unit') {
                setEffectCue({
                  kind: 'avoid',
                  phase: event.succeeded ? 'positive' : 'negative',
                  targetInstanceId: event.target.instanceId,
                });
              }
              setCellCue({
                cellId: event.targetCellId,
                kind: 'avoid',
                phase: event.succeeded ? 'positive' : 'negative',
              });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              setCellCue(undefined);
              setEffectCue(undefined);
              break;
            case 'AttackResolved':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              // A successful sidestep still has a physical destination: the
              // attacker lunges into the now-empty cell, but the cancelled
              // group emits no DamageDealt and therefore needs its own visual
              // beat.  Deliberately omit target player/card ids so Board uses
              // targetCellId rather than a player HUD or the moved-away card.
              if (event.avoided === true && event.amount === 0) {
                const attacker = draft.instances[event.attackerId];
                if (attacker?.zone === 'board' && attacker.cellId !== undefined) {
                  const missCue = {
                    attackGroupId: event.attackGroupId,
                    sourceInstanceId: attacker.id,
                    sourceCellId: attacker.cellId,
                    targetCellId: event.targetCellId,
                    amount: 0,
                  };
                  setCombatCue({ ...missCue, phase: 'travel' });
                  await delay(timing().attackTravel);
                  setCombatCue({ ...missCue, phase: 'impact' });
                  await delay(timing().attackImpact);
                  setCombatCue(undefined);
                }
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hiddenHudStep);
              setNotice(undefined);
              break;
            case 'CardDrawn': {
              applyEvent(draft, event);
              if (previewedReactionEvents.has(event)) {
                // The causal preview already materialized the card and played
                // its hand flight at the parent event.  CardDrawn itself is a
                // reducer no-op, so keep the authoritative stream position
                // without a second late animation.
                showState(structuredClone(draft));
                break;
              }
              // The card must be in the hand for the cue to animate anything:
              // the hand's draw is keyed on the cue naming a rendered card.
              materializeDrawnInstance(draft, batch.state, event.playerId, event.instanceId);
              // Privacy snapshots deliberately omit the paired deck→hand
              // CardMoved. CardDrawn is the viewer-safe, semantic boundary
              // that identifies both the receiving player and the new card.
              //
              // The cue is patched BEFORE the state so the hand can never see
              // the card without it. Both land in one commit while React batches
              // them, but should they ever split, this is the harmless order: a
              // cue naming a card that is not in the hand yet simply waits,
              // whereas the reverse paints the card in its slot for a frame and
              // then fades it out as its own flight departs from the deck.
              setDrawCue({
                playerId: event.playerId,
                instanceId: event.instanceId,
                sequence: ++drawCueSequenceRef.current,
              });
              showState(structuredClone(draft));
              if (event.sourceDefId !== undefined) setNotice(noticeFor(event, labelPlayer, viewerSide));
              // Every drawn card owns a beat. The cue is a single scalar, so
              // without a wait several CardDrawn in a row (drawCards(2), or both
              // players' upkeep in an unredacted feed) collapse into one React
              // batch and the hand only ever sees the last one. An opponent draw
              // is animated nowhere, so it must not hold up the tempo.
              const drawnForViewer = viewerIdRef.current === undefined
                || event.playerId === viewerIdRef.current;
              await delay(drawnForViewer
                ? timing().cardDraw
                : timing().hiddenHudStep);
              if (event.sourceDefId !== undefined) setNotice(undefined);
              // The cue lives exactly its own window. Left set, it stays in the
              // store for the rest of the match and replays the draw-in on any
              // remount of the hand.
              setDrawCue(undefined);
              break;
            }
            case 'DamageDealt': {
              const avoidance = event.origin === 'ordinary_attack'
                && event.combat === true
                && event.sourceId !== undefined
                && (event.partIndex ?? 0) === 0
                ? successfulAvoidancesBySourceId.get(event.sourceId)
                : undefined;
              const avoidedSummary = avoidance === undefined
                ? undefined
                : orderedEvents.slice(eventIndex + 1).find((candidate): candidate is Extract<GameEvent, { type: 'AttackResolved' }> =>
                  candidate.type === 'AttackResolved'
                  && !pairedAvoidedAttackResolvedEvents.has(candidate)
                  && candidate.attackGroupId === avoidance.attackGroupId
                  && candidate.attackerId === avoidance.attackerId
                  && candidate.avoided === true
                  && candidate.amount === 0);
              const reciprocal = avoidance === undefined && event.origin === 'ordinary_attack'
                ? orderedEvents.slice(eventIndex + 1).find((candidate): candidate is Extract<GameEvent, { type: 'DamageDealt' }> =>
                  candidate.type === 'DamageDealt'
                  && !pairedDamageEvents.has(candidate)
                  && areReciprocalOrdinaryCombatParts(event, candidate))
                : undefined;
              if (reciprocal !== undefined) {
                const sourceCellId = sourceCellFor(draft, knownCells, event);
                const reciprocalSourceCellId = sourceCellFor(draft, knownCells, reciprocal);
                const sourceIsOnBoard = event.sourceId !== undefined
                  && draft.instances[event.sourceId]?.zone === 'board'
                  && draft.instances[event.sourceId]?.cellId !== undefined
                  && !departedIds.has(event.sourceId);
                const reciprocalSourceIsOnBoard = reciprocal.sourceId !== undefined
                  && draft.instances[reciprocal.sourceId]?.zone === 'board'
                  && draft.instances[reciprocal.sourceId]?.cellId !== undefined
                  && !departedIds.has(reciprocal.sourceId);
                if (sourceCellId !== undefined && reciprocalSourceCellId !== undefined
                  && sourceIsOnBoard && reciprocalSourceIsOnBoard
                  && event.sourceId !== undefined && reciprocal.sourceId !== undefined
                  && event.target.kind === 'unit' && reciprocal.target.kind === 'unit'
                  && opposing(sourceCellId) === reciprocalSourceCellId) {
                  pairedDamageEvents.add(reciprocal);
                  const reciprocalEntry = entryByEvent.get(reciprocal);
                  if (reciprocalEntry !== undefined) revealLogEntry(reciprocalEntry);
                  const primaryStrike = {
                    ...(event.attackGroupId === undefined ? {} : { attackGroupId: event.attackGroupId }),
                    sourceInstanceId: event.sourceId,
                    sourceCellId,
                    ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                    targetInstanceId: event.target.instanceId,
                    amount: event.amount,
                    ...(event.partIndex === undefined ? {} : { partIndex: event.partIndex }),
                    ...(event.partCount === undefined ? {} : { partCount: event.partCount }),
                  };
                  const reciprocalStrike = {
                    ...(reciprocal.attackGroupId === undefined ? {} : { attackGroupId: reciprocal.attackGroupId }),
                    sourceInstanceId: reciprocal.sourceId,
                    sourceCellId: reciprocalSourceCellId,
                    ...(reciprocal.targetCellId === undefined ? {} : { targetCellId: reciprocal.targetCellId }),
                    targetInstanceId: reciprocal.target.instanceId,
                    amount: reciprocal.amount,
                    ...(reciprocal.partIndex === undefined ? {} : { partIndex: reciprocal.partIndex }),
                    ...(reciprocal.partCount === undefined ? {} : { partCount: reciprocal.partCount }),
                  };
                  setCombatCue({
                    ...primaryStrike,
                    phase: 'travel',
                    reciprocal: { ...reciprocalStrike, phase: 'travel' },
                  });
                  await delay(timing().attackTravel);
                  for (const damage of [event, reciprocal]) {
                    applyEvent(draft, damage);
                    const targetId = damage.target.kind === 'unit' ? damage.target.instanceId : undefined;
                    if (damage.lethal === true && targetId !== undefined && deathIdsInBatch.has(targetId)) {
                      fracture(targetId);
                      explainedLethalIds.add(targetId);
                    }
                  }
                  showState(structuredClone(draft));
                  setCombatCue({
                    ...primaryStrike,
                    phase: 'impact',
                    reciprocal: { ...reciprocalStrike, phase: 'impact' },
                  });
                  await delay(timing().attackImpact);
                  setCombatCue(undefined);
                  for (const damage of [event, reciprocal]) {
                    const targetId = damage.target.kind === 'unit' ? damage.target.instanceId : undefined;
                    if (damage.lethal === true && targetId !== undefined && deathIdsInBatch.has(targetId)) {
                      // Keep the zero-health card mounted for exactly the
                      // impact beat so its crack and dissolve can complete,
                      // then release the cell before later attacks play.
                      depart(targetId);
                    }
                  }
                  for (const damage of [event, reciprocal]) {
                    const beforeShield = damage.amount + (damage.absorbed ?? 0);
                    if (damage.baseAmount === undefined || damage.baseAmount === beforeShield) continue;
                    if (damage.target.kind !== 'unit') continue;
                    setEffectCue({
                      kind: damage.baseAmount > beforeShield ? 'shield' : 'effect',
                      phase: damage.baseAmount > beforeShield ? 'impact' : 'positive',
                      targetInstanceId: damage.target.instanceId,
                    });
                    setNotice(noticeFor(damage, labelPlayer, viewerSide));
                    await delay(timing().statusChange);
                    setNotice(undefined);
                    setEffectCue(undefined);
                  }
                  for (const damage of [event, reciprocal]) {
                    const targetId = damage.target.kind === 'unit' ? damage.target.instanceId : undefined;
                    if (damage.lethal === true && targetId !== undefined && deathIdsInBatch.has(targetId)) {
                      await playEarlyDecayBurst(targetId, eventIndex);
                    }
                  }
                  if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
                  if (reactivePreviewsByParent.has(reciprocal)) await playReactivePreviews(reciprocal);
                  for (const damage of [event, reciprocal]) {
                    if (damage.sourceId !== undefined && hackCuesByAttackerRef.current.has(damage.sourceId)) {
                      hackCuesByAttackerRef.current.delete(damage.sourceId);
                    }
                  }
                  setHackCue(undefined);
                  break;
                }
              }
              const selectedHackCue = event.sourceId === undefined
                ? undefined
                : hackCuesByAttackerRef.current.get(event.sourceId);
              if (selectedHackCue !== undefined) setHackCue(selectedHackCue);
              const sourceCellId = sourceCellFor(draft, knownCells, event);
              const targetInstanceId = event.target.kind === 'unit' ? event.target.instanceId : undefined;
              const targetPlayerId = event.target.kind === 'player' ? event.target.playerId : undefined;
              const lethalDeathId = event.lethal === true
                && targetInstanceId !== undefined
                && deathIdsInBatch.has(targetInstanceId)
                ? targetInstanceId
                : undefined;
              const applyDamageEvent = (): void => {
                applyEvent(draft, event);
                if (lethalDeathId !== undefined) {
                  fracture(lethalDeathId);
                }
                showState(structuredClone(draft));
              };
              const sourceIsOnBoard = event.sourceId !== undefined
                && draft.instances[event.sourceId]?.zone === 'board'
                && draft.instances[event.sourceId]?.cellId !== undefined
                && !departedIds.has(event.sourceId);
              const deathStrikeDefId = event.sourceId !== undefined && fracturedIds.has(event.sourceId)
                ? draft.instances[event.sourceId]?.defId ?? batch.state.instances[event.sourceId]?.defId
                : undefined;
              const castSourceDefId = event.sourceId === undefined || sourceIsOnBoard
                ? undefined
                : revealedSourceDefs.get(event.sourceId);
              const avoidedAttacker = avoidance === undefined
                ? undefined
                : draft.instances[avoidance.attackerId];
              const avoidedSourceCellId = avoidedAttacker?.cellId ?? knownCells.get(avoidance?.attackerId ?? '');
              const avoidedSourceIsOnBoard = avoidedAttacker?.zone === 'board'
                && avoidedAttacker.cellId !== undefined
                && !departedIds.has(avoidance?.attackerId ?? '');
              const canPlayDodgedWaveTogether = avoidedSummary !== undefined
                && avoidance !== undefined
                && avoidedSourceCellId !== undefined
                && avoidedSourceIsOnBoard
                && sourceCellId !== undefined
                && sourceIsOnBoard
                && event.sourceId !== undefined
                && event.targetCellId !== undefined;
              if (activeOneShot !== undefined && event.sourceId === activeOneShot.instanceId) {
                setNotice(noticeFor(event, labelPlayer, viewerSide));
              }
              // A living board source uses the physical lunge. A source that
              // has already died instead drops its public glyph onto the target.
              if (canPlayDodgedWaveTogether) {
                const primaryStrike = {
                  ...(event.attackGroupId === undefined ? {} : { attackGroupId: event.attackGroupId }),
                  sourceInstanceId: event.sourceId!,
                  sourceCellId: sourceCellId!,
                  targetCellId: event.targetCellId!,
                  ...(targetInstanceId === undefined ? {} : { targetInstanceId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                  amount: event.amount,
                  ...(event.partIndex === undefined ? {} : { partIndex: event.partIndex }),
                  ...(event.partCount === undefined ? {} : { partCount: event.partCount }),
                };
                const missedStrike = {
                  attackGroupId: avoidance!.attackGroupId,
                  sourceInstanceId: avoidance!.attackerId,
                  sourceCellId: avoidedSourceCellId!,
                  targetCellId: avoidedSummary!.targetCellId,
                  amount: 0,
                  partIndex: 0,
                  partCount: 1,
                };
                pairedAvoidedAttackResolvedEvents.add(avoidedSummary!);
                const avoidedEntry = entryByEvent.get(avoidedSummary!);
                if (avoidedEntry !== undefined) revealLogEntry(avoidedEntry);
                setCombatCue({ ...primaryStrike, phase: 'travel', parallel: { ...missedStrike, phase: 'travel' } });
                await delay(timing().attackTravel);
                applyDamageEvent();
                applyEvent(draft, avoidedSummary!);
                showState(structuredClone(draft));
                setCombatCue({ ...primaryStrike, phase: 'impact', parallel: { ...missedStrike, phase: 'impact' } });
                await delay(timing().attackImpact);
                setCombatCue(undefined);
              } else if (
                sourceCellId !== undefined
                && sourceIsOnBoard
                && deathStrikeDefId === undefined
                && (event.targetCellId !== undefined || targetPlayerId !== undefined)
              ) {
                setCombatCue({
                  ...(event.attackGroupId === undefined ? {} : { attackGroupId: event.attackGroupId }),
                  sourceInstanceId: event.sourceId!,
                  sourceCellId,
                  ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                  ...(targetInstanceId !== undefined ? { targetInstanceId } : {}),
                  ...(targetPlayerId !== undefined ? { targetPlayerId } : {}),
                  amount: event.amount,
                  ...(event.partIndex === undefined ? {} : { partIndex: event.partIndex }),
                  ...(event.partCount === undefined ? {} : { partCount: event.partCount }),
                  phase: 'travel',
                });
                await delay(timing().attackTravel);
                applyDamageEvent();
                setCombatCue({
                  ...(event.attackGroupId === undefined ? {} : { attackGroupId: event.attackGroupId }),
                  sourceInstanceId: event.sourceId!,
                  sourceCellId,
                  ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                  ...(targetInstanceId !== undefined ? { targetInstanceId } : {}),
                  ...(targetPlayerId !== undefined ? { targetPlayerId } : {}),
                  amount: event.amount,
                  ...(event.partIndex === undefined ? {} : { partIndex: event.partIndex }),
                  ...(event.partCount === undefined ? {} : { partCount: event.partCount }),
                  phase: 'impact',
                });
                await delay(timing().attackImpact);
                setCombatCue(undefined);
              } else if (sourceCellId !== undefined && (event.targetCellId !== undefined || targetPlayerId !== undefined)) {
                setEffectPathCue({
                  kind: deathStrikeDefId === undefined ? 'trigger' : 'death-fall',
                  sourceCellId,
                  ...(deathStrikeDefId === undefined ? {} : { sourceDefId: deathStrikeDefId }),
                  ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                  ...(targetInstanceId === undefined ? {} : { targetInstanceId }),
                  ...(event.amount > 0 ? { amount: event.amount } : {}),
                  phase: 'travel',
                });
                await delay(timing().effectTravel);
                applyDamageEvent();
                setEffectPathCue({
                  kind: deathStrikeDefId === undefined ? 'trigger' : 'death-fall',
                  sourceCellId,
                  ...(deathStrikeDefId === undefined ? {} : { sourceDefId: deathStrikeDefId }),
                  ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                  ...(targetInstanceId === undefined ? {} : { targetInstanceId }),
                  ...(event.amount > 0 ? { amount: event.amount } : {}),
                  phase: 'impact',
                });
                setEffectCue({
                  kind: 'damage', phase: 'impact',
                  ...(targetInstanceId === undefined ? {} : { targetInstanceId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                });
                await delay(timing().castImpact);
                setEffectCue(undefined);
                setEffectPathCue(undefined);
              } else if (castSourceDefId !== undefined && (event.targetCellId !== undefined || targetPlayerId !== undefined)) {
                setCastCue({
                  sourceDefId: castSourceDefId,
                  ...(event.sourceId === undefined ? {} : { sourceInstanceId: event.sourceId }),
                  ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                  ...(event.amount > 0 ? { amount: event.amount } : {}),
                  phase: 'travel',
                });
                await delay(timing().effectTravel);
                applyDamageEvent();
                setCastCue({
                  sourceDefId: castSourceDefId,
                  ...(event.sourceId === undefined ? {} : { sourceInstanceId: event.sourceId }),
                  ...(event.targetCellId === undefined ? {} : { targetCellId: event.targetCellId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                  ...(event.amount > 0 ? { amount: event.amount } : {}),
                  phase: 'impact',
                });
                setEffectCue({
                  kind: 'damage', phase: 'impact',
                  ...(targetInstanceId === undefined ? {} : { targetInstanceId }),
                  ...(targetPlayerId === undefined ? {} : { targetPlayerId }),
                });
                await delay(timing().castImpact);
                setEffectCue(undefined);
                setCastCue(undefined);
              } else {
                applyDamageEvent();
                setEffectCue({
                  kind: 'damage',
                  phase: 'impact',
                  ...(targetInstanceId !== undefined ? { targetInstanceId } : {}),
                  ...(targetPlayerId !== undefined ? { targetPlayerId } : {}),
                });
                // Sourceless effect damage straight to a player is what a deck
                // passive like Rising Damage produces: pulse the opposing owner's
                // badge so the escalating hit reads as coming from the passive.
                const risingSourceId = targetPlayerId !== undefined && event.sourceId === undefined
                  ? Object.keys(draft.players).find((id) => id !== targetPlayerId
                    && deckPassiveIdForPlayer(draft, id) === 'rising_damage')
                  : undefined;
                if (risingSourceId !== undefined) {
                  setPassivePulse({ playerId: risingSourceId, variant: 'discharge' });
                }
                if (targetInstanceId !== undefined || targetPlayerId !== undefined) {
                  await delay(timing().statusChange);
                }
                setEffectCue(undefined);
                setPassivePulse(undefined);
              }
              if (lethalDeathId !== undefined) {
                // All visible damage paths above hold an impact cue while the
                // fractured card dissolves. Remove it only after that cue, not
                // at the same instant HP reaches zero.
                depart(lethalDeathId);
              }
              const beforeShield = event.amount + (event.absorbed ?? 0);
              if (event.baseAmount !== undefined && event.baseAmount !== beforeShield) {
                setEffectCue({
                  kind: event.baseAmount > beforeShield ? 'shield' : 'effect',
                  phase: event.baseAmount > beforeShield ? 'impact' : 'positive',
                  ...(targetInstanceId !== undefined ? { targetInstanceId } : {}),
                  ...(targetPlayerId !== undefined ? { targetPlayerId } : {}),
                });
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                await delay(timing().statusChange);
                setNotice(undefined);
                setEffectCue(undefined);
              }
              if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
              if (lethalDeathId !== undefined) explainedLethalIds.add(lethalDeathId);
              if (lethalDeathId !== undefined) await playEarlyDecayBurst(lethalDeathId, eventIndex);
              if (event.sourceId !== undefined && selectedHackCue !== undefined) {
                hackCuesByAttackerRef.current.delete(event.sourceId);
                setHackCue(undefined);
              }
              if (activeOneShot !== undefined && event.sourceId === activeOneShot.instanceId) setNotice(undefined);
              break;
            }
            case 'PlayerHealthRestored': {
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({
                kind: 'heal', phase: 'positive', targetPlayerId: event.playerId, delta: event.amount,
              });
              // The shown health count-tweens up on its own; the badge pulse is
              // what names Steady Recovery as the source of the round-end heal.
              // Other heals use the same explicit green pulse, so a health
              // number does not quietly jump in the middle of another chain.
              const healingPassive = deckPassiveIdForPlayer(draft, event.playerId);
              if (healingPassive === 'steady_recovery' && event.sourceId === undefined && event.amount > 0) {
                setPassivePulse({ playerId: event.playerId, variant: 'heal', delta: event.amount });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              setPassivePulse(undefined);
              break;
            }
            case 'UnitHealed': {
              if (previewedReactionEvents.has(event)) break;
              const sourceCellId = event.sourceId === undefined
                ? undefined
                : draft.instances[event.sourceId]?.cellId ?? knownCells.get(event.sourceId);
              const targetCellId = draft.instances[event.instanceId]?.cellId ?? knownCells.get(event.instanceId);
              // A heal issued by the card currently revealing has an actor and
              // a receiver just like a positive aura. Use the same physical
              // lunge as that buff, but land a green heal rather than damage.
              // This is semantic (reveal-source + attributed heal), so every
              // authored on-appear heal receives the same readable treatment.
              const revealHealStrike = event.sourceId !== undefined
                && activeAppearanceSource === event.sourceId
                && sourceCellId !== undefined
                && targetCellId !== undefined
                && sourceCellId !== targetCellId
                ? {
                    sourceInstanceId: event.sourceId,
                    sourceCellId,
                    targetCellId,
                    targetInstanceId: event.instanceId,
                    amount: 0,
                  }
                : undefined;
              if (revealHealStrike !== undefined) {
                setCombatCue({ ...revealHealStrike, phase: 'travel' });
                await delay(timing().attackTravel);
                applyEvent(draft, event);
                showState(structuredClone(draft));
                setCombatCue({ ...revealHealStrike, phase: 'impact' });
                setEffectCue({
                  kind: 'heal', phase: 'positive', targetInstanceId: event.instanceId, delta: event.amount,
                });
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                await delay(timing().attackImpact);
                setNotice(undefined);
                setEffectCue(undefined);
                setCombatCue(undefined);
                if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
                break;
              }
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (sourceCellId !== undefined && targetCellId !== undefined && sourceCellId !== targetCellId) {
                setEffectPathCue({
                  kind: 'heal', sourceCellId, targetCellId,
                  targetInstanceId: event.instanceId, amount: event.amount, phase: 'travel',
                });
                await delay(timing().effectTravel);
                setEffectPathCue({
                  kind: 'heal', sourceCellId, targetCellId,
                  targetInstanceId: event.instanceId, amount: event.amount, phase: 'impact',
                });
              }
              setEffectCue({
                kind: 'heal', phase: 'positive', targetInstanceId: event.instanceId, delta: event.amount,
              });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              setEffectPathCue(undefined);
              if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
              break;
            }
            case 'UnitMoved':
              // Relocate first, then let the board FLIP-slide the card from its
              // old cell to the new one (see MoveCue): a single continuous glide
              // rather than a fade-out here followed by a fade-in there. moveCue
              // is set in the same batch as showState so the destination card is
              // already mounted when the board measures the travel delta.
              applyEvent(draft, event);
              knownCells.set(event.instanceId, event.to);
              setMoveCue({ instanceId: event.instanceId, fromCellId: event.from });
              showState(structuredClone(draft));
              if (activeOneShot !== undefined) {
                setNotice(noticeFor(event, labelPlayer, viewerSide));
              }
              await delay(timing().unitMove);
              setNotice(undefined);
              setMoveCue(undefined);
              break;
            case 'StatChanged': {
              // One growth reaches the client as several events: `+1/+1` is
              // attack, then maxHealth, then health, because ops.modifyStat
              // keeps the journal literal — one record per call. Played one at
              // a time that is three anonymous `+1` over the same spot for
              // three beats, and the maxHealth one is indistinguishable from
              // the health one, so a `+1/+1` reads as `+1/+2`. Take the whole
              // run for this card and play it as a single ±ATK/±HP beat. Every
              // event is still applied in order and still reveals its own log
              // line; only contiguous, independent presentation is merged.
              const statRun = [event];
              for (let i = eventIndex + 1; i < orderedEvents.length; i += 1) {
                const next = orderedEvents[i]!;
                if (next.type !== 'StatChanged' || next.instanceId !== event.instanceId) break;
                if (previewedReactionEvents.has(next)) break;
                // Adjacent mutations can belong to separate reactions.  Only
                // merge an identical causal boundary (or two ordinary,
                // unannotated stream facts), never pull one reaction into the
                // visual beat of another.
                if (next.causedByEventIndex !== event.causedByEventIndex) break;
                // A gain and a loss in one window would need two colours and
                // two directions at once; opposite signs keep separate beats.
                if ((next.delta >= 0) !== (event.delta >= 0)) break;
                statRun.push(next);
              }
              for (const change of statRun) {
                applyEvent(draft, change);
                if (change === event) continue;
                mergedStatEvents.add(change);
                const mergedEntry = entryByEvent.get(change);
                if (mergedEntry !== undefined) revealLogEntry(mergedEntry);
              }
              showState(structuredClone(draft));
              // An overlay receiver absorbs its host's stats while still off
              // the board (between reveal and placement): there is no board
              // card to pulse, so the usual beat would stall on an empty cell.
              // Apply silently — the card flips up already carrying the stats.
              if (activeResolutionSource?.instanceId === event.instanceId
                && draft.instances[event.instanceId]?.cellId === undefined) break;
              const sumStat = (stat: string): number => statRun.reduce(
                (total, change) => (change.stat === stat ? total + change.delta : total), 0);
              const attackDelta = sumStat('attack');
              const healthDelta = sumStat('health');
              // The card shows current health, so a maxHealth-only change moves
              // no number: then the chip over the heart is the only sign of it.
              const shownHealthDelta = healthDelta !== 0 ? healthDelta : sumStat('maxHealth');
              setEffectCue({
                kind: attackDelta === 0 && shownHealthDelta > 0 ? 'heal' : 'stat',
                phase: event.delta >= 0 ? 'positive' : 'negative',
                targetInstanceId: event.instanceId,
                statDeltas: {
                  ...(attackDelta !== 0 ? { attack: attackDelta } : {}),
                  ...(shownHealthDelta !== 0 ? { health: shownHealthDelta } : {}),
                },
              });
              if (activeOneShot !== undefined) {
                setNotice(noticeFor(event, labelPlayer, viewerSide));
              }
              await delay(timing().statBuff);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            }
            case 'EffectAdded':
              if (shouldDeferFieldEntryEffect(event)) {
                const sourceId = event.effect.sourceId!;
                const deferred = deferredFieldEntryEffects.get(sourceId) ?? [];
                deferred.push(event);
                deferredFieldEntryEffects.set(sourceId, deferred);
                break;
              }
              await playEffectAdded(event);
              break;
            case 'EffectCounterChanged': {
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (previewedReactionEvents.has(event)) break;
              if (event.scope.kind === 'unit' && !isMaturationCounterSpend(event)) {
                setEffectCue({
                  kind: event.reason === 'photosynthesis' ? 'sun' : 'counter',
                  phase: event.delta >= 0 ? 'positive' : 'negative',
                  targetInstanceId: event.scope.instanceId,
                  delta: event.delta,
                });
              } else if (event.scope.kind === 'cell') {
                setCellCue({
                  cellId: event.scope.cellId,
                  kind: 'effect',
                  phase: event.delta >= 0 ? 'positive' : 'negative',
                  delta: event.delta,
                });
              } else if (event.scope.kind === 'player'
                && deckPassiveIdForPlayer(draft, event.scope.playerId) === event.defId) {
                // Rising Damage keeps its X on a player-scoped counter: growth
                // charges the badge, a reset (taking damage) drains it.
                setPassivePulse({
                  playerId: event.scope.playerId,
                  variant: event.reason === 'player_damaged' ? 'reset' : 'charge',
                  ...(event.delta !== 0 ? { delta: event.delta } : {}),
                });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              setCellCue(undefined);
              setPassivePulse(undefined);
              break;
            }
            case 'EffectRemoved': {
              const removed = draft.effects.find((effect) => effect.id === event.effectId);
              // ops.absorbCards strips every effect carried by (or sourced from)
              // a donor BEFORE consuming it, so a receiver eating three buffed
              // slimes opened with three unexplained "buff off" pulses on cards
              // still standing, and only then pulled them in — 420 ms each, and
              // present or absent depending on whether the prey happened to be
              // buffed. The effect leaves because its carrier is about to be
              // eaten whole, which is what the swallow itself says. Apply
              // silently. (Keyed on the donor, so an unrelated removal earlier
              // in the batch on a card absorbed later also stays quiet: one lost
              // pulse on a card eaten moments later beats a phantom phase in
              // front of every absorption.)
              const strippedByAbsorption = removed !== undefined
                && ((removed.scope.kind === 'unit' && absorptionReceiverByDonor.has(removed.scope.instanceId))
                  || (removed.sourceId !== undefined && absorptionReceiverByDonor.has(removed.sourceId)));
              if (previewedReactionEvents.has(event) || strippedByAbsorption) {
                applyEvent(draft, event);
                showState(structuredClone(draft));
                break;
              }
              if (removed?.scope.kind === 'unit') {
                setEffectCue({ kind: 'effect', phase: 'remove', targetInstanceId: removed.scope.instanceId });
                await delay(timing().statusChange);
              } else if (removed?.scope.kind === 'player') {
                setEffectCue({ kind: 'effect', phase: 'remove', targetPlayerId: removed.scope.playerId });
                await delay(timing().statusChange);
              } else if (removed?.scope.kind === 'cell') {
                setCellCue({ cellId: removed.scope.cellId, kind: 'effect', phase: 'remove' });
                await delay(timing().cellPulse);
              }
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue(undefined);
              setCellCue(undefined);
              break;
            }
            case 'ReactionTriggered':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              break;
            case 'AttachmentAdded':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({ kind: 'virus', phase: 'attach', targetInstanceId: event.attachment.hostInstanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'AttachmentTriggered':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({ kind: 'virus', phase: 'trigger', targetInstanceId: event.hostInstanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'AttachmentRemoved': {
              const attachment = draft.attachments.find((item) => item.attachmentId === event.attachmentId);
              if (attachment !== undefined) {
                setEffectCue({ kind: 'virus', phase: 'remove', targetInstanceId: attachment.hostInstanceId });
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                await delay(timing().statusChange);
              }
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            }
            case 'CaptureApplied':
              setEffectCue({ kind: 'capture', phase: 'departure', targetInstanceId: event.instanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().captureTransfer);
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({ kind: 'capture', phase: 'arrival', targetInstanceId: event.instanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'CaptureReturned':
              setEffectCue({ kind: 'capture', phase: 'departure', targetInstanceId: event.instanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().captureTransfer);
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({ kind: 'capture', phase: 'arrival', targetInstanceId: event.instanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'UnitAppeared':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (activeAppearanceSource === event.instanceId) activeAppearanceSource = undefined;
              break;
            case 'CaptureFizzled':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({ kind: 'capture', phase: 'negative', targetInstanceId: event.instanceId });
              setNotice(m14PlaybackNotice(event));
              await delay(timing().explanation);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'CardResolved': {
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (event.kind === 'SPELL' || event.kind === 'VIRUS') {
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                await delay(timing().explanation);
                setNotice(undefined);
                setOneShotLineup((cards) => cards.filter((card) => card.instanceId !== event.instanceId));
                activeOneShot = undefined;
              }
              if (activeResolutionSource?.instanceId === event.instanceId) activeResolutionSource = undefined;
              break;
            }
            case 'CardFizzled': {
              applyEvent(draft, event);
              // Engine 31 pays for a failed FIELD intent and discards it
              // (roundWorkflow `field_reveal`). That hand→discard is part of
              // THIS beat: replayed by the generic CardMoved case it landed
              // after the notice had already cleared, so the card was quietly
              // subtracted from the hand with nothing on screen to explain it.
              let fizzleDiscard: GameEvent | undefined;
              if (event.kind === 'FIELD' && draft.instances[event.instanceId]?.zone === 'hand') {
                for (let i = eventIndex + 1; i < orderedEvents.length; i += 1) {
                  const upcoming = orderedEvents[i]!;
                  if (upcoming.type === 'CardRevealed' || upcoming.type === 'CardFizzled'
                    || upcoming.type === 'CombatStarted') break;
                  if (upcoming.type === 'CardMoved' && upcoming.instanceId === event.instanceId
                    && upcoming.from === 'hand' && upcoming.to === 'discard') {
                    fizzleDiscard = upcoming;
                    spentFizzleDiscards.add(upcoming);
                    break;
                  }
                }
              }
              if (event.kind === 'FIELD') {
                const cellId = pendingFieldBackCells.get(event.instanceId);
                if (cellId !== undefined) {
                  const flipSwap = Math.round(timing().cardReveal * 0.6);
                  setFieldFizzleCue({ instanceId: event.instanceId, cellId });
                  setEnteringId(event.instanceId);
                  setFaceDownIds([event.instanceId]);
                  releaseFieldBack(event.instanceId);
                  setLineupHandoffIds([event.instanceId]);
                  showState(structuredClone(draft));
                  await delay(flipSwap);
                  setFaceDownIds([]);
                  await delay(timing().cardReveal - flipSwap);
                  setEnteringId(undefined);
                  setLineupHandoffIds([]);
                  setEffectCue({
                    kind: 'effect',
                    phase: 'negative',
                    targetInstanceId: event.instanceId,
                  });
                } else {
                  releaseFieldBack(event.instanceId);
                  showState(structuredClone(draft));
                }
              } else {
                showState(structuredClone(draft));
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              setEffectCue(undefined);
              // The card leaves the hand on the frame its fizzle card starts
              // leaving the cell: one departure, not two unexplained ones. The
              // cell copy then fades out over its own short beat instead of being
              // unmounted mid-frame — it is a card the player has just been shown,
              // and blinking it out of existence read as a glitch.
              if (fizzleDiscard !== undefined) applyEvent(draft, fizzleDiscard);
              const fizzleCue = store.getVisuals().fieldFizzleCue;
              if (fizzleCue?.instanceId === event.instanceId) {
                setFieldFizzleCue({ ...fizzleCue, leaving: true });
                if (fizzleDiscard !== undefined) showState(structuredClone(draft));
                await delay(timing().fieldFizzleFade);
              } else if (fizzleDiscard !== undefined) {
                showState(structuredClone(draft));
              }
              setFieldFizzleCue(undefined);
              setOneShotLineup((cards) => cards.filter((card) => card.instanceId !== event.instanceId));
              // A fizzle is the other terminal outcome of a play, so it releases
              // the overlay host ghost the same way the flip does. Today an
              // overlay receiver that eats its host and then never reaches its
              // appearance (removed_before_appearance) has no cell in the target
              // snapshot, so the ghost was never raised for it — but the cue is
              // keyed on the CELL, and leaving it standing would flip the next
              // card revealed there with the trap-shut keyframe.
              overlayHostGhost = undefined;
              setHostAbsorbCue(undefined);
              activeOneShot = undefined;
              if (activeResolutionSource?.instanceId === event.instanceId) activeResolutionSource = undefined;
              break;
            }
            case 'HackApplied':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setEffectCue({ kind: 'hack', phase: 'attach', targetInstanceId: event.marker.victimInstanceId });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setEffectCue(undefined);
              break;
            case 'HackTriggered': {
              applyEvent(draft, event);
              const baseCue = {
                hackId: event.hackId,
                attackerId: event.attackerId,
                initiatorPlayerId: event.initiatorPlayerId,
                candidates: event.candidates,
              };
              setHackCue({ ...baseCue, selected: undefined, phase: 'candidates' });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hackCandidates);
              const selectedCue: HackCue = { ...baseCue, selected: event.selected, phase: 'selected' };
              hackCuesByAttackerRef.current.set(event.attackerId, selectedCue);
              setHackCue(selectedCue);
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hackSelected);
              setNotice(undefined);
              setHackCue(undefined);
              break;
            }
            case 'HackRemoved':
              if (event.reason !== 'triggered') {
                setEffectCue({ kind: 'hack', phase: 'remove', targetInstanceId: event.victimInstanceId });
              }
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (event.reason !== 'triggered') {
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                await delay(timing().statusChange);
                setNotice(undefined);
              }
              setEffectCue(undefined);
              break;
            case 'RandomUnitSelectionResolved': {
              applyEvent(draft, event);
              showState(structuredClone(draft));
              const baseCue = {
                ...(event.sourceId === undefined ? {} : { sourceId: event.sourceId }),
                candidates: event.candidates,
              };
              setRandomPickCue({ ...baseCue, selected: undefined, phase: 'candidates' });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hackCandidates);
              setRandomPickCue({ ...baseCue, selected: event.selected, phase: 'selected' });
              await delay(timing().hackSelected);
              setNotice(undefined);
              setRandomPickCue(undefined);
              break;
            }
            case 'TokenCreated': {
              if (burstConsumedTokenIds.delete(event.instanceId)) break;
              const pendingDeath = pendingDeaths.at(-1);
              const sourceDefId = pendingDeath === undefined
                ? undefined
                : draft.instances[pendingDeath.event.instanceId]?.defId;
              const decayFragment = pendingDeath?.event.cellId !== undefined
                && event.cellId !== undefined
                && sourceDefId !== undefined
                && getCardUi(sourceDefId)?.mechanics?.includes('decay') === true
                && getCardUi(event.defId)?.mechanics?.includes('fragment') === true;
              if (decayFragment) {
                // The whole decay burst plays as one moment: every fragment of
                // this death launches simultaneously and flies to its cell
                // while the source's death animation runs alongside; removal
                // of the source stays deferred until the chain ends. The scan
                // reads through the fragments' own follower events
                // (UnitEnteredPlay/UnitAppeared) but leaves them in place —
                // they replay later as cheap no-delay applications.
                const burst = [event];
                const burstIds = new Set([event.instanceId]);
                for (let i = eventIndex + 1; i < orderedEvents.length; i += 1) {
                  const next = orderedEvents[i]!;
                  if (next.type === 'TokenCreated' && next.cellId !== undefined
                    && getCardUi(next.defId)?.mechanics?.includes('fragment') === true) {
                    burst.push(next);
                    burstIds.add(next.instanceId);
                    burstConsumedTokenIds.add(next.instanceId);
                    continue;
                  }
                  if ((next.type === 'UnitEnteredPlay' || next.type === 'UnitAppeared')
                    && burstIds.has(next.instanceId)) continue;
                  break;
                }
                for (const created of burst) {
                  applyEvent(draft, created);
                  animatedTokenIds.add(created.instanceId);
                  knownCells.set(created.instanceId, created.cellId!);
                }
                setDyingId(pendingDeath!.event.instanceId);
                deathAnimationsStartedWithSpawn.add(pendingDeath!.event.instanceId);
                setSpawnCue({
                  sourceCellId: pendingDeath!.event.cellId!,
                  targetInstanceIds: burst.map((created) => created.instanceId),
                  decayFragment: true,
                });
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                setEnteringId(event.instanceId);
                showState(structuredClone(draft));
                await delay(timing().decayFragment);
                setEnteringId(undefined);
                setSpawnCue(undefined);
                setNotice(undefined);
                break;
              }
              applyEvent(draft, event);
              animatedTokenIds.add(event.instanceId);
              if (event.cellId !== undefined) {
                knownCells.set(event.instanceId, event.cellId);
                setCellCue({ cellId: event.cellId, kind: 'creation', phase: 'positive' });
                const deathSource = pendingDeath?.event.cellId;
                if (pendingDeath !== undefined && deathSource !== undefined) {
                  setSpawnCue({ sourceCellId: deathSource, targetInstanceIds: [event.instanceId] });
                }
              }
              if (deckPassiveIdForPlayer(draft, event.ownerId) === 'echoing_deployment') {
                setPassivePulse({ playerId: event.ownerId, variant: 'echo' });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              if (event.cellId !== undefined) setEnteringId(event.instanceId);
              showState(structuredClone(draft));
              await delay(timing().tokenCreate);
              setEnteringId(undefined);
              setSpawnCue(undefined);
              setNotice(undefined);
              setCellCue(undefined);
              setPassivePulse(undefined);
              break;
            }
            case 'TokenCreationSkipped':
              applyEvent(draft, event);
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              break;
            case 'CardCreated':
              applyEvent(draft, event);
              // A card conjured straight into the hand arrives the same way a
              // drawn one does, so it flies the same trip in from off-screen:
              // the hand's draw-in is keyed on nothing but this cue, and without
              // it the card simply popped into its slot between two frames.
              // Unlike CardDrawn there is nothing to materialize — the reducer
              // above is a real mutation that already seated the card in the
              // hand. The cue still precedes the state for the same reason: a
              // cue naming a card the hand has not mounted yet waits, whereas
              // the reverse paints the slot for a frame and then fades it out
              // under its own departing flight.
              setDrawCue({
                playerId: event.ownerId,
                instanceId: event.instanceId,
                sequence: ++drawCueSequenceRef.current,
              });
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              // The creation notice keeps its full reading window; the flight
              // fits inside it, and the cue only has to outlive the first frame
              // the hand renders it on.
              await delay(timing().explanation);
              setNotice(undefined);
              setDrawCue(undefined);
              break;
            case 'CardConsumed': {
              const assembly = assemblyByFragmentId.get(event.instanceId);
              if (assembly !== undefined) {
                if (playedAssemblyFragments.has(event.instanceId)) break;
                const [firstId, secondId] = assembly.fragmentIds;
                playedAssemblyFragments.add(firstId);
                playedAssemblyFragments.add(secondId);
                setAssemblyCue({ fragmentIds: assembly.fragmentIds, robotCellId: assembly.robotCellId });
                setConsumingIds((current) => [...new Set([...current, firstId, secondId])]);
                setNotice(noticeFor(assembly, labelPlayer, viewerSide));
                await delay(timing().consume);
                for (const consumed of orderedEvents) {
                  if (consumed.type === 'CardConsumed' && assembly.fragmentIds.includes(consumed.instanceId)) {
                    applyEvent(draft, consumed);
                  }
                }
                showState(structuredClone(draft));
                setConsumingIds((current) => current.filter((id) => !assembly.fragmentIds.includes(id)));
                setAssemblyCue(undefined);
                setNotice(undefined);
                break;
              }
              const receiverId = consumptionReceiverByDonor.get(event.instanceId);
              const receiverCellId = receiverId === undefined
                ? undefined
                : draft.instances[receiverId]?.cellId
                  ?? batch.state.instances[receiverId]?.cellId
                  ?? knownCells.get(receiverId);
              // Overlay host: the receiver flips up in this very cell on the
              // next beat, lying ON this host. The ghost standing in for the
              // host takes over its exact box and then waits, motionless, for
              // that flip to finish (its animation is delayed by cardReveal) —
              // so the flip is played on a still meal, and only what is left of
              // the host above the risen card is swallowed under it.
              //
              // Two phases. `expose` names the meal while the host is still the
              // real occupant: both sides have been looking at it under a
              // face-down back since the lineup, and the notice gets its own
              // beat before anything is grabbed. Only then does the ghost take
              // over — a host that is grabbed on the frame its name appears
              // reads as a rendering fault rather than as a meal.
              if (receiverId !== undefined
                && draft.instances[receiverId]?.cellId === undefined
                && receiverCellId === event.cellId) {
                // Snapshot before applyEvent: the ghost keeps wearing the face
                // — and the numbers — the host had when it was grabbed.
                const grabbed = draft.instances[event.instanceId] === undefined
                  ? undefined
                  : effectiveStats(draft, event.instanceId);
                const hostFace = {
                  cellId: event.cellId,
                  defId: event.defId,
                  ...(grabbed === undefined ? {} : {
                    stats: { attack: grabbed.attack, health: Math.max(0, grabbed.health) },
                  }),
                } as const;
                setHostAbsorbCue({ ...hostFace, phase: 'expose' });
                setNotice(noticeFor(event, labelPlayer, viewerSide));
                await delay(timing().overlayHostExpose);
                overlayHostGhost = { ...hostFace, phase: 'swallow' };
                setHostAbsorbCue(overlayHostGhost);
                applyEvent(draft, event);
                showState(structuredClone(draft));
                break;
              }
              if (receiverCellId !== undefined && receiverCellId !== event.cellId) {
                setEffectPathCue({
                  kind: 'absorb',
                  sourceCellId: event.cellId,
                  targetCellId: receiverCellId,
                  ...(receiverId === undefined ? {} : { targetInstanceId: receiverId }),
                  phase: 'travel',
                });
              }
              setConsumingIds((current) => [...current, event.instanceId]);
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().consume);
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setConsumingIds((current) => current.filter((id) => id !== event.instanceId));
              setNotice(undefined);
              setEffectPathCue(undefined);
              break;
            }
            case 'CardsAbsorbed': {
              applyEvent(draft, event);
              showState(structuredClone(draft));
              // An overlay receiver absorbs its host while still off-board:
              // the host's consume animation just played and the flip is the
              // very next visible beat. A notice over the vacated cell would
              // only stall the flow — the summed stats land with the flip.
              // What the swallow moved is instead attached to the host ghost,
              // which floats it out of the gulp; without that, the receiver
              // simply flips up bigger than its printed stats with nothing
              // saying where the difference came from.
              // (Scoped to the active FIELD source: redacted absorb summaries
              // for cards hidden from this viewer must still play their beat.)
              if (activeResolutionSource?.instanceId === event.receiver.instanceId
                && draft.instances[event.receiver.instanceId]?.cellId === undefined) {
                if (overlayHostGhost !== undefined
                  && (event.totals.attack > 0 || event.totals.health > 0)) {
                  overlayHostGhost = {
                    ...overlayHostGhost,
                    gained: { attack: event.totals.attack, health: event.totals.health },
                  };
                  setHostAbsorbCue(overlayHostGhost);
                }
                if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
                break;
              }
              const receiverCellId = draft.instances[event.receiver.instanceId]?.cellId
                ?? batch.state.instances[event.receiver.instanceId]?.cellId
                ?? knownCells.get(event.receiver.instanceId);
              if (receiverCellId !== undefined) {
                setCellCue({ cellId: receiverCellId, kind: 'trigger', phase: 'positive' });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              setCellCue(undefined);
              if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
              break;
            }
            case 'RandomDamageReplacementResolved':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (event.original.targetCellId !== undefined) {
                setCellCue({
                  cellId: event.original.targetCellId,
                  kind: 'avoid',
                  phase: event.succeeded ? 'positive' : 'negative',
                });
              }
              if (event.original.target.kind === 'unit') {
                setEffectCue({
                  kind: 'avoid',
                  phase: event.succeeded ? 'positive' : 'negative',
                  targetInstanceId: event.original.target.instanceId,
                });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              setCellCue(undefined);
              setEffectCue(undefined);
              break;
            case 'CardTransformed':
              setTransformingId(event.instanceId);
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              applyEvent(draft, event);
              showState(structuredClone(draft));
              await delay(timing().transform);
              setTransformingId(undefined);
              setNotice(undefined);
              break;
            case 'AssemblyCompleted':
              applyEvent(draft, event);
              setCellCue({ cellId: event.robotCellId, kind: 'assembly', phase: 'positive' });
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              setCellCue(undefined);
              break;
            case 'TemporaryEnergyGranted':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (deckPassiveIdForPlayer(draft, event.playerId) === 'energy_reserve') {
                setPassivePulse({ playerId: event.playerId, variant: 'reserve', delta: event.amount });
              }
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              setPassivePulse(undefined);
              break;
            case 'TemporaryEnergyExpired':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              break;
            case 'TimedDestroyScheduled':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().explanation);
              setNotice(undefined);
              break;
            case 'TimedDestroyResolved':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().statusChange);
              setNotice(undefined);
              break;
            case 'RoundEnded':
              applyEvent(draft, event);
              showState(structuredClone(draft));
              currentStage = 'round_end';
              setBanner('round_end');
              setNotice(noticeFor(event, labelPlayer, viewerSide));
              await delay(timing().hiddenHudStep);
              setNotice(undefined);
              break;
            case 'UnitDied':
              if (currentStage !== 'deaths') {
                currentStage = 'deaths';
                setBanner('deaths');
              }
              knownCells.set(event.instanceId, event.cellId);
              const devourerId = event.cause === 'destroyed'
                ? devourerByPrey.get(event.instanceId)
                : undefined;
              const devourerCellId = devourerId === undefined
                ? undefined
                : draft.instances[devourerId]?.cellId
                  ?? batch.state.instances[devourerId]?.cellId
                  ?? knownCells.get(devourerId);
              if (devourerCellId !== undefined && devourerCellId !== event.cellId) {
                setEffectPathCue({
                  kind: 'devour',
                  sourceCellId: devourerCellId,
                  targetCellId: event.cellId,
                  targetInstanceId: event.instanceId,
                  phase: 'travel',
                });
                await delay(timing().devour);
                // The pull-in keyframe ends with the prey held invisible
                // inside the devourer, so release its cell BEFORE clearing
                // the cue: once the devour class comes off, an unmounted
                // card cannot flash back at full size for a frame.
                devouredIds.add(event.instanceId);
                depart(event.instanceId);
                setEffectPathCue(undefined);
              }
              if (!explainedLethalIds.has(event.instanceId)
                && activeOneShot !== undefined
                && activeResolutionSource?.instanceId === activeOneShot.instanceId) {
                setCastCue({
                  sourceDefId: activeResolutionSource.defId,
                  sourceInstanceId: activeResolutionSource.instanceId,
                  targetCellId: event.cellId,
                  phase: 'travel',
                });
                await delay(timing().effectTravel);
                setCastCue({
                  sourceDefId: activeResolutionSource.defId,
                  sourceInstanceId: activeResolutionSource.instanceId,
                  targetCellId: event.cellId,
                  phase: 'impact',
                });
                setEffectCue({ kind: 'effect', phase: 'negative', targetInstanceId: event.instanceId });
                await delay(timing().castImpact);
                setEffectCue(undefined);
                setCastCue(undefined);
              }
              fracture(event.instanceId);
              if (reactivePreviewsByParent.has(event)) await playReactivePreviews(event);
              pendingDeaths.push({ event, followers: [] });
              break;
            case 'CardMoved':
              // The draw already moved this card so its animation could start on
              // the CardDrawn beat; replaying the move would throw on the zone.
              if (event.from === 'deck' && event.to === 'hand'
                && draft.instances[event.instanceId]?.zone === 'hand') break;
              if (event.from === 'board') {
                const pending = pendingDeaths.find((candidate) => candidate.event.instanceId === event.instanceId);
                if (pending !== undefined) {
                  pending.followers.push(event);
                  break;
                }
              }
              if (event.to === 'board') hydrateRevealedInstance(draft, batch.state, event.instanceId);
              applyEvent(draft, event);
              showState(structuredClone(draft));
              if (activeOneShot?.instanceId === event.instanceId && event.from === 'armed') activeOneShot = undefined;
              break;
            case 'TokenRemoved': {
              const pending = pendingDeaths.find((candidate) => candidate.event.instanceId === event.instanceId);
              if (pending !== undefined) {
                pending.followers.push(event);
                break;
              }
              applyEvent(draft, event);
              showState(structuredClone(draft));
              break;
            }
            default:
              {
                const lifecycleNotice = m14PlaybackNotice(event);
                if (lifecycleNotice !== undefined) {
                  applyEvent(draft, event);
                  showState(structuredClone(draft));
                  setNotice(lifecycleNotice);
                  await delay(timing().explanation);
                  setNotice(undefined);
                  break;
                }
              }
              applyEvent(draft, event);
          }
        }
        await dissolvePendingDeaths();
      } catch {
        // A redacted event stream can intentionally omit private setup facts.
        // The authoritative snapshot is still the safe fallback, but it must
        // not look like an unexplained late game action.
        setNotice({ kind: 'signal', text: 'Визуализация восстановлена по состоянию матча' });
        await delay(timing().explanation);
        setNotice(undefined);
      }
      const publicVarCues = changedPublicVarCues(draft, batch.state);
      if (publicVarCues.length > 0 && mountedRef.current) {
        showState(batch.state);
        for (const cue of publicVarCues) {
          setEffectCue({ kind: 'effect', phase: cue.phase, targetInstanceId: cue.instanceId });
          await delay(timing().statusChange);
          setEffectCue(undefined);
        }
      }
      // A playback fallback or skip must never leave accepted events hidden.
      for (const entry of batch.entries) revealLogEntry(entry);
      setBanner(undefined);
      setEnteringId(undefined);
      setFaceDownIds([]);
      setOneShotLineup([]);
      setFaceDownCells([]);
      setWithheldFieldEntryIds([]);
      setLineupHandoffIds([]);
      setFieldFizzleCue(undefined);
      setFracturedIds([]);
      setDepartedIds([]);
      setDyingId(undefined);
      setConsumingIds([]);
      setTransformingId(undefined);
      setTimedDestroyingId(undefined);
      setNotice(undefined);
      setCombatCue(undefined);
      setEffectCue(undefined);
      setEffectPathCue(undefined);
      setCastCue(undefined);
      setCellCue(undefined);
      setMoveCue(undefined);
      setSpawnCue(undefined);
      setHostAbsorbCue(undefined);
      setAssemblyCue(undefined);
      setHackCue(undefined);
      setRandomPickCue(undefined);
      setPassivePulse(undefined);
      hackCuesByAttackerRef.current.clear();
      showState(batch.state);
      // A skipped batch jumped straight to its result: ring what it changed
      // for a moment, so the outcome is still read (03 §5).
      if (skipRef.current && mountedRef.current) {
        const changes = skippedChanges(batchStart, batch.state);
        if (changes.instanceIds.length > 0 || changes.playerIds.length > 0) {
          store.patch({ skipChanges: changes });
          skipChangesTimerRef.current = setTimeout(() => {
            skipChangesTimerRef.current = undefined;
            store.patch({ skipChanges: undefined });
          }, MOTION_DURATIONS.afterSkip);
        }
      }
      setIsPlaying(false);
    }
    drainingRef.current = false;
  }, [delay, revealLogEntry, showState]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
      if (skipChangesTimerRef.current !== undefined) clearTimeout(skipChangesTimerRef.current);
      delayResolverRef.current?.();
    };
  }, []);

  useEffect(() => {
    const previousQueuedSeq = lastQueuedSeqRef.current;
    const advancesPlayback = seq > lastQueuedSeqRef.current;
    const batchEntries = advancesPlayback
      ? log.filter((entry) => entry.seq === seq)
      : [];
    for (const entry of batchEntries) pendingLogIndicesRef.current.add(entry.index);

    // Reconnect history and older capped entries do not replay. Reconcile them
    // immediately, while keeping the current playback batch hidden until each
    // corresponding visual step starts.
    const authoritativeIndices = new Set(log.map((entry) => entry.index));
    const immediateEntries = log.filter((entry) =>
      !pendingLogIndicesRef.current.has(entry.index)
      && entry.seq <= previousQueuedSeq);
    const reconciledIndices = new Set(
      [...visibleLogIndicesRef.current].filter((index) => authoritativeIndices.has(index)),
    );
    for (const entry of immediateEntries) reconciledIndices.add(entry.index);
    visibleLogIndicesRef.current = reconciledIndices;
    store.updateVisibleLog((current) => {
      const next = current.filter((entry) => authoritativeIndices.has(entry.index));
      const nextIndices = new Set(next.map((entry) => entry.index));
      for (const entry of immediateEntries) {
        if (nextIndices.has(entry.index)) continue;
        next.push(entry);
        nextIndices.add(entry.index);
      }
      return next.length === current.length && next.every((entry, index) => entry === current[index])
        ? current
        : next;
    });

    if (!advancesPlayback) return;
    queueRef.current.push({ state: authoritativeState, entries: batchEntries });
    lastQueuedSeqRef.current = seq;
    void drain();
  }, [authoritativeState, drain, log, seq, store]);

  return store;
}
